import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useGame, api } from '../context/GameContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { Coins, Star, Gift, ChevronRight, ChevronDown, ExternalLink, Clock, Wrench, User, Image, Flame, Sparkles, ExternalLink as LinkIcon, Check, Crop, Hammer, X, Layers, RefreshCw } from 'lucide-react';
import CardRevealModal from '../components/CardRevealModal';
import RandomPaintingReel from '../components/RandomPaintingReel';
import SellModal from '../components/SellModal';
import LiveCoinPill from '../components/LiveCoinPill';
import TeamPanel from '../components/TeamPanel';
import RestorerWalker from '../components/RestorerWalker';
import RestorerHireSheet from '../components/RestorerHireSheet';
import RestorerRenameSheet from '../components/RestorerRenameSheet';
import PRManagerSheet from '../components/PRManagerSheet';
import PRManagerCardPicker from '../components/PRManagerCardPicker';
import GameTooltip from '../components/GameTooltip';
import TutorialArrow from '../components/TutorialArrow';
import TutorialDialog from '../components/TutorialDialog';
import ChessRook from '../components/ChessRook';
import CropEditor from '../components/CropEditor';
import TonSparkles from '../components/TonSparkles';
import CardBuffPanel from '../components/CardBuffPanel';
import CollectionsPanel from '../components/CollectionsPanel';
import SectionLoader from '../components/SectionLoader';
import thumb from '../utils/thumb';


// Influence level calculation (mirrors backend)
const getInfluenceLevel = (xp) => {
  let level = 1;
  let totalNeeded = 0;
  while (true) {
    const nextCost = level * 2 + level * level;
    if (xp < totalNeeded + nextCost) break;
    totalNeeded += nextCost;
    level++;
  }
  return level;
};

const getInfluenceXpForLevel = (level) => {
  let total = 0;
  for (let l = 1; l < level; l++) total += l * 2 + l * l;
  return total;
};

const getInfluenceNextCost = (level) => level * 2 + level * level;

const generateXpPreview = () => {
  const xpRanges = {3: [6, 10], 6: [11, 15], 9: [16, 22]};
  const result = {};
  [3, 6, 9].forEach(h => {
    const [min, max] = xpRanges[h];
    result[h] = min + Math.floor(Math.random() * (max - min + 1));
  });
  return result;
};

// Apply influence_income bonus to XP preview
const applyInfluenceBonus = (baseXp, user) => {
  const bonus = 1 + (user?.upgrades?.influence_income || 0) * 0.01;
  return Math.round(baseXp * bonus);
};

const RARITY_COLORS = {
  common: '#9CA3AF',
  uncommon: '#93C5FD',
  rare: '#3B82F6',
  legendary: '#A855F7',
  masterpiece: '#D4AF37',
};

const RARITY_LABELS = {
  common: { en: 'Common', ru: 'Обычная', zh: '普通' },
  uncommon: { en: 'Uncommon', ru: 'Необычная', zh: '非凡' },
  rare: { en: 'Rare', ru: 'Редкая', zh: '稀有' },
  legendary: { en: 'Legendary', ru: 'Легендарная', zh: '传奇' },
  masterpiece: { en: 'Masterpiece', ru: 'Шедевр', zh: '杰作' },
};

// Sort options shown when user has > 4 paintings.
// Order matters: first option is the default. Localised labels live inline
// in the chip below since translations.js is global and we don't want to
// pollute it with one-off keys.
const RARITY_SORT_RANK = {
  masterpiece: 4, legendary: 3, rare: 2, uncommon: 1, common: 0,
};

// Pending exhibit income for the live coin counter — mirrors backend
// `compute_exhibit_pending`. Returns coins earned-so-far on an active
// exhibit as a **float** (not floored): with typical 30-min exhibits
// earning ~20 coins the floor-rounded value only ticks once every ~90
// seconds, which doesn't feel like the coins are "dripping". A
// floating-point value with one decimal updates visibly every few
// seconds — much closer to the idle/clicker feel the player asked for.
// The frontend formats this with one decimal place when summed against
// the integer server balance. The server still credits whole coins
// only (via `apply_pending_exhibit_commit` floor logic), which means
// at commit time the displayed `100.4` snaps back to integer `100`
// with the previously-accrued amount carried into the server total —
// no visible jump.
export const computeExhibitPendingCoins = (card) => {
  if (!card?.exhibit_ends || !card?.exhibit_start) return 0;
  const startTs = new Date(card.exhibit_start).getTime();
  const endTs = new Date(card.exhibit_ends).getTime();
  if (!(endTs > startTs)) return 0;
  const elapsed = Math.max(0, Math.min(endTs - startTs, Date.now() - startTs));
  const progress = elapsed / (endTs - startTs);
  const total = card.exhibit_expected_reward || 0;
  // Subtract the portion the backend has already drip-credited so we
  // only show the un-synced delta. Without this the per-card overlay
  // double-counts everything that's already in `user.coins`.
  const paid = card.exhibit_coins_paid || 0;
  return Math.max(0, total * progress - paid);
};

// Live durability calculator — pure function reused inside the
// gallery grid and the detail modal. Two modes:
//   1) Painting on exhibit → linearly interpolate from start durability
//      down to (start - expected_decay) over the exhibit window. The
//      player sees wear ticking down in real time instead of one big
//      drop at claim. Authoritative value is still re-computed by
//      `/collect-exhibit` on the backend.
//   2) Painting idle → apply the standard per-hour decay from
//      `durability_updated_at`. Mirrors backend `calc_durability`.
export const computeLiveDurability = (card, durabilitySlowLevel = 0) => {
  if (!card) return 100;
  const isNft = !!card.nft_address;
  const floor = isNft ? 1 : 0;
  if (card.exhibit_ends) {
    const endTs = new Date(card.exhibit_ends).getTime();
    const startTs = card.exhibit_start ? new Date(card.exhibit_start).getTime() : null;
    const startDur = card.exhibit_start_durability ?? card.durability ?? 100;
    const expectedDecay = card.exhibit_expected_decay ?? 0;
    if (startTs && endTs > startTs) {
      const total = endTs - startTs;
      const elapsed = Math.max(0, Math.min(total, Date.now() - startTs));
      const progress = elapsed / total;
      return Math.max(floor, startDur - expectedDecay * progress);
    }
    return Math.max(floor, startDur);
  }
  const dur = card.durability ?? 100;
  const updatedAt = card.durability_updated_at;
  if (!updatedAt) return Math.max(floor, dur);
  const hours = (Date.now() - new Date(updatedAt).getTime()) / 3600000;
  if (hours <= 0) return Math.max(floor, dur);
  const slowBonus = (durabilitySlowLevel || 0) * 0.01;
  // Match the server's per-card decay multiplier so idle decay
  // interpolates at the EXACT same rate the backend records (~[0.75,
  // 1.25] × base). Without this, cards rolled < 1.0× appeared to
  // decay slower than the server actually applied — the user
  // complaint "стало меньше снимать". Server falls back to 1.0
  // when no card_id is available, mirror that fallback here.
  const mult = typeof card.decay_multiplier === 'number' && card.decay_multiplier > 0
    ? card.decay_multiplier
    : 1.0;
  const decayPerHour = (5 / 24) * (1 - slowBonus) * mult;
  return Math.max(floor, dur - hours * decayPerHour);
};

// "Value" used by the price-sort and the on-card star badge.
// Formula: max(auction_won_amount, influence_level × 10). Taking the
// max means an auction-bought painting never falls below its paid
// price, but if the owner levels it up past that point the displayed
// value catches up properly (no more "level 10 card frozen at the 30★
// auction price"). The same helper feeds the sort comparator so the
// gallery order stays consistent with the badge.
const cardValue = (c) => {
  const auction = (c?.auction_won_amount && c.auction_won_amount > 0) ? c.auction_won_amount : 0;
  const levelValue = getInfluenceLevel(c?.influence_xp || 0) * 10;
  return Math.max(auction, levelValue);
};

const SORT_OPTIONS = [
  { id: 'availability', label_en: 'Availability', label_ru: 'Доступные' },
  { id: 'value',  label_en: 'By value',   label_ru: 'По стоимости' },
  { id: 'rarity', label_en: 'By rarity',  label_ru: 'По редкости' },
  { id: 'recent', label_en: 'Newest',     label_ru: 'Сначала новые' },
  { id: 'original', label_en: 'Original',  label_ru: 'Обычный' },
];
const SORT_THRESHOLD = 4; // show chip only when collection exceeds this

const GalleryPage = ({ firstCard, onFirstCardShown }) => {
  const { user, t, refreshUser, language, haptic, tutorialStep, setTutorialStep, setUser, publishActiveExhibits, flashCoinReward, consumePrefetch } = useGame();
  const location = useLocation();
  const navigate = useNavigate();
  // Boot-time prefetch consumed once-per-mount. GameContext fetched
  // these endpoints in parallel with auth, so the gallery can show
  // real data immediately instead of waiting for the mount-time
  // loadCards round trip.
  const bootCards = React.useMemo(
    () => (consumePrefetch ? consumePrefetch('/cards/me') : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const bootStats = React.useMemo(
    () => (consumePrefetch ? consumePrefetch('/cards/stats') : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const bootPool = React.useMemo(
    () => (consumePrefetch ? consumePrefetch('/cards/preview-pool') : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const bootValue = React.useMemo(
    () => (consumePrefetch ? consumePrefetch('/users/me/collection-value') : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // Session cache for instant render on tab re-visits. Mirrors the
  // pattern in EarnPage — sessionStorage is per-tab and wiped on full
  // reload, which matches the freshness we want (navigate away and
  // back → instant; hard reload → fresh).
  const galleryCacheKey = user?.id ? `galleryCache:${user.id}` : null;
  const galleryCached = React.useMemo(() => {
    if (!galleryCacheKey) return null;
    try {
      const raw = sessionStorage.getItem(galleryCacheKey);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  }, [galleryCacheKey]);

  const initialCards = firstCard
    ? [firstCard]
    : (bootCards || galleryCached?.cards || []);
  const initialAvailable = bootStats
    ? (bootStats.available_count > 0)
    : (galleryCached?.hasAvailable ?? false);

  const [userCards, setUserCards] = useState(initialCards);
  const [hasAvailableCards, setHasAvailableCards] = useState(initialAvailable);
  const [loading, setLoading] = useState(!firstCard && !bootCards && !galleryCached);
  const [selectedCard, setSelectedCardRaw] = useState(null);
  const [newCard, setNewCard] = useState(null);
  const [buying, setBuying] = useState(false);
  const xpCacheRef = useRef({});
  // Tracks previous durability per card to detect repair deltas — when
  // the backend's lazy Restorer tick bumps a card's durability, we flash
  // a tiny "+N" floating label next to its bar so the player gets a
  // crisp visual signal that someone is actually fixing things in the
  // background, not just an animated NPC.
  const prevDurabilityRef = useRef({});
  const [repairFlashes, setRepairFlashes] = useState({}); // { cardId: { delta, ts } }
  const getXpForCard = (cardId) => {
    if (!xpCacheRef.current[cardId]) {
      xpCacheRef.current[cardId] = generateXpPreview();
    }
    return xpCacheRef.current[cardId];
  };
  const [exhibiting, setExhibiting] = useState(null);
  const [collecting, setCollecting] = useState(null);

  // Wheel-of-fortune reel: when player taps "Buy random painting", we open
  // this modal and the actual /cards/buy-random POST happens inside the
  // reel after the spin. `reelPurchasedCard` holds the card returned by
  // the server so we can open the detail modal once the reel closes.
  const [showReel, setShowReel] = useState(false);
  const [reelTutorial, setReelTutorial] = useState(false);
  const [reelPurchasedCard, setReelPurchasedCard] = useState(null);
  // Restorer state — derived from `user.team_restorer_hired_until` (real
  // backend-persisted hire). The visual walker is active iff hired & not
  // expired. `restorerActive` keeps a boolean we tick every second so the
  // walker stops the moment the shift ends.
  const [restorerActive, setRestorerActive] = useState(false);
  const [hireSheetOpen, setHireSheetOpen] = useState(false);
  const [renameSheetOpen, setRenameSheetOpen] = useState(false);
  // PR Manager (Molly) sheet state. The full state object — level, xp,
  // slots, assignments, eligible-for-PR cards — comes from
  // `/api/team/pr-manager/me` and is refreshed when the sheet opens
  // or an assignment commits. The picker is a stacked sub-sheet
  // (chosen card resolves via `prPickerResolverRef`).
  const [prState, setPrState] = useState(null);
  const [prSheetOpen, setPrSheetOpen] = useState(false);
  const [prRenameOpen, setPrRenameOpen] = useState(false);
  const [prPickerOpen, setPrPickerOpen] = useState(false);
  const prPickerResolverRef = useRef(null);
  const prPickerExcludeRef = useRef(new Set());
  // Live queue emitted by RestorerWalker: [{id, image_url, title, status, progress}]
  // status ∈ 'walking' | 'working' | 'idle' | 'queued'.
  const [restorerQueue, setRestorerQueue] = useState([]);
  // Player-customisable nickname for the Restorer NPC. Stored on the
  // user document on the backend (1 ⭐ per change). Falls back to a
  // legacy localStorage value (free) for players who set a name
  // before the paid rename flow shipped, then to "Jeff".
  const restorerName = useMemo(() => {
    const fromUser = (user?.team_restorer_name || '').trim();
    if (fromUser) return fromUser;
    try {
      const fromLocal = (localStorage.getItem('team_restorer_name') || '').trim();
      if (fromLocal) return fromLocal;
    } catch { /* ignore */ }
    return 'Jeff';
  }, [user?.team_restorer_name]);
  const prManagerName = (user?.team_pr_manager_name || '').trim() || 'Molly';
  const prManagerLevel = user?.team_pr_manager_level || 1;
  const prManagerUnlocked = (user?.cards_count || 0) >= 2;
  const prManagerSlotsTotal = useMemo(() => {
    const lvl = prManagerLevel;
    if (lvl >= 30) return 5;
    if (lvl >= 22) return 4;
    if (lvl >= 15) return 3;
    if (lvl >= 8) return 2;
    return 1;
  }, [prManagerLevel]);
  const prManagerSlotsUsed = (prState?.assignments || []).length;

  // Pull Molly's full state on demand (when the sheet opens) and
  // after each mutation so the slot count + level stay in sync.
  const refreshPrState = useCallback(async () => {
    try {
      const res = await api.get('/team/pr-manager/me');
      setPrState(res.data);
      if (res.data?.user) setUser(res.data.user);
    } catch (e) {
      // Soft-fail — the sheet just shows empty data; main UI keeps working.
      console.error('PR Manager state load failed:', e);
    }
  }, [setUser]);
  useEffect(() => {
    if (!prSheetOpen) return;
    refreshPrState();
  }, [prSheetOpen, refreshPrState]);
  // Also refresh the slot-badge once the team panel is visible so the
  // user sees an accurate "0/1 · L1" without having to open the sheet.
  useEffect(() => {
    if (prManagerUnlocked) refreshPrState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prManagerUnlocked]);

  const hiredUntil = user?.team_restorer_hired_until || null;
  useEffect(() => {
    const recompute = () => {
      const v = !!(hiredUntil && new Date(hiredUntil).getTime() > Date.now());
      setRestorerActive(v);
    };
    recompute();
    // Restorer contracts last days at minimum — a per-second poll just
    // to flip the active flag is wasted CPU. 30s cadence is plenty
    // (and recompute also fires on hire/extend via the deps change).
    const id = setInterval(recompute, 30000);
    return () => clearInterval(id);
  }, [hiredUntil]);
  // Cached preview-pool for the reel — prefetched as soon as we know the
  // user is on the gallery page so that opening the reel is instant
  // (avoids the 5s "Loading…" flash on slow Telegram networks). Stored
  // as the full payload `{ templates, uncommons, rares }` so decorative
  // sprinkles work on the very first reel open.
  const [reelPool, setReelPool] = useState(
    bootPool
      ? {
          templates: bootPool.templates || [],
          uncommons: bootPool.uncommons || [],
          rares: bootPool.rares || [],
          legendaries: bootPool.legendaries || [],
        }
      : { templates: [], uncommons: [], rares: [], legendaries: [] }
  );
  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    api.get('/cards/preview-pool').then(res => {
      if (cancelled) return;
      setReelPool({
        templates: res.data?.templates || [],
        uncommons: res.data?.uncommons || [],
        rares: res.data?.rares || [],
        legendaries: res.data?.legendaries || [],
      });
    }).catch(() => { /* silently degrade — reel will fetch on its own */ });
    return () => { cancelled = true; };
    // Re-run whenever the player's collection changes — newly-owned templates
    // must drop out of the pool.
  }, [user?.id, userCards.length]);

  // Live collection value (Σ stars + USD equivalent) — shown as a pill
  // in the header. Refreshed whenever the card list changes so it stays
  // in sync after exhibits, repairs, auction wins, etc.
  const [collectionValue, setCollectionValue] = useState(bootValue || null);
  useEffect(() => {
    let cancelled = false;
    if (!user?.id) return;
    api.get('/users/me/collection-value').then(res => {
      if (!cancelled) setCollectionValue(res.data);
    }).catch(() => { /* silently degrade — pill just hides */ });
    return () => { cancelled = true; };
  }, [user?.id, userCards.length]);

  // Card detail modal uses a fullscreen overlay (position:fixed). The
  // underlying document scroll position naturally stays put, so we
  // intentionally do NOT touch body/html styles or window.scrollY —
  // every attempt to "lock" the scroll introduced jumps or flashes.
  // This mirrors how the TOP page handles its preview modal.
  const setSelectedCard = useCallback((next) => {
    setSelectedCardRaw(next);
  }, []);

  // Paintings | Collections — paintings is the default view.
  const [galleryTab, setGalleryTab] = useState('paintings');


  // Gallery sort — only matters when the collection is large enough that
  // scrolling becomes a chore. Persist user's pick across sessions.
  const [sortBy, setSortBy] = useState(() => {
    try {
      const saved = localStorage.getItem('gallery_sort');
      if (saved && SORT_OPTIONS.some(o => o.id === saved)) return saved;
    } catch (_) { /* localStorage unavailable in some webviews */ }
    return 'value';
  });
  const persistSort = (id) => {
    setSortBy(id);
    try { localStorage.setItem('gallery_sort', id); } catch (_) { /* noop */ }
  };
  const sortedCards = useMemo(() => {
    if (!userCards || userCards.length === 0) return userCards;
    const arr = [...userCards];
    // Stable: put exhibiting cards on top regardless of sort — those
    // demand player attention and used to be the implicit top-of-grid hint.
    const exhibitingFirst = (a, b) => {
      const aEx = a.exhibit_ends && new Date(a.exhibit_ends) > new Date() ? 1 : 0;
      const bEx = b.exhibit_ends && new Date(b.exhibit_ends) > new Date() ? 1 : 0;
      return bEx - aEx;
    };
    // Inverse for "availability" — push exhibiting to the bottom so the
    // player sees what they CAN act on right now.
    const availableFirst = (a, b) => {
      const aEx = a.exhibit_ends && new Date(a.exhibit_ends) > new Date() ? 1 : 0;
      const bEx = b.exhibit_ends && new Date(b.exhibit_ends) > new Date() ? 1 : 0;
      return aEx - bEx;
    };
    const acquiredAsc = (a, b) =>
      new Date(a.acquired_at || a.created_at || 0) - new Date(b.acquired_at || b.created_at || 0);
    if (sortBy === 'value') {
      arr.sort((a, b) => exhibitingFirst(a, b) || cardValue(b) - cardValue(a));
    } else if (sortBy === 'rarity') {
      arr.sort((a, b) =>
        exhibitingFirst(a, b)
        || (RARITY_SORT_RANK[b.rarity] || 0) - (RARITY_SORT_RANK[a.rarity] || 0)
        || cardValue(b) - cardValue(a)
      );
    } else if (sortBy === 'availability') {
      // Free cards on top, exhibits at the bottom. Within each group the
      // order matches acquisition order so the same card stays put as
      // exhibits start/finish.
      arr.sort((a, b) => availableFirst(a, b) || acquiredAsc(a, b));
    } else if (sortBy === 'original') {
      // Pure acquisition order — no exhibit-first treatment, no ranking.
      arr.sort(acquiredAsc);
    } else { // 'recent'
      arr.sort((a, b) => exhibitingFirst(a, b) || (
        new Date(b.acquired_at || b.created_at || 0) - new Date(a.acquired_at || a.created_at || 0)
      ));
    }
    return arr;
  }, [userCards, sortBy]);

  // Reward animation state — coin pill animation now lives inside
  // <LiveCoinPill /> (subscribed to the external coin store), so the
  // page itself no longer re-renders per flash. Influence pops stay
  // local (they fire rarely).
  const [influencePulse, setInfluencePulse] = useState(false);
  const [floatingInfluence, setFloatingInfluence] = useState(null);

  const triggerCoinReward = useCallback((amount, opts = {}) => {
    flashCoinReward(amount, opts);
  }, [flashCoinReward]);

  const triggerInfluenceReward = useCallback((amount) => {
    setInfluencePulse(true);
    setTimeout(() => setInfluencePulse(false), 800);
    setFloatingInfluence(amount);
    setTimeout(() => setFloatingInfluence(null), 1500);
  }, []);

  // Live durability refresh while Restorer is active. The backend is the
  // source of truth for durability; the visual walker is decorative.
  // Poll `/cards/me` every 30s so the player sees durability tick up as
  // the Restorer works in the background. We intentionally use the
  // silent variant so the loading spinner doesn't flash.
  useEffect(() => {
    if (!restorerActive) return;
    const id = setInterval(() => {
      loadCards(true);
    }, 30000);
    return () => clearInterval(id);
    // loadCards is stable enough — we only want to (re)start polling on
    // toggle changes, not on every cards-array mutation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restorerActive]);

  // Diff durability between successive `userCards` updates and emit a
  // short-lived "+N" flash entry per card whose durability went up.
  // Repairs from the user's own /repair button also pass through here,
  // but those snap to 100 and would flash a huge delta — clamp the
  // shown delta to single-digit increments so the in-place repair UX
  // doesn't get noisy.
  useEffect(() => {
    if (!userCards || userCards.length === 0) return;
    const prev = prevDurabilityRef.current;
    const next = {};
    const newFlashes = {};
    const now = Date.now();
    for (const c of userCards) {
      const cur = c.durability ?? 100;
      next[c.id] = cur;
      const before = prev[c.id];
      if (before != null && cur > before + 0.05) {
        const delta = Math.min(99, cur - before);
        // Only flash modest increments (Restorer ticks are 1–3 dur over
        // a poll interval). Big jumps from manual repair are skipped.
        if (delta <= 8) {
          newFlashes[c.id] = { delta, ts: now };
        }
      }
    }
    prevDurabilityRef.current = next;
    if (Object.keys(newFlashes).length > 0) {
      setRepairFlashes((cur) => ({ ...cur, ...newFlashes }));
      // Auto-clear each flash after 2.2s (matches CSS animation duration).
      const ids = Object.keys(newFlashes);
      const timeout = setTimeout(() => {
        setRepairFlashes((cur) => {
          const out = { ...cur };
          for (const id of ids) {
            if (out[id]?.ts === newFlashes[id].ts) delete out[id];
          }
          return out;
        });
      }, 2200);
      return () => clearTimeout(timeout);
    }
  }, [userCards]);

  // Open preview for the first onboarding card
  useEffect(() => {
    if (firstCard && userCards.length > 0) {
      const match = userCards.find(c => c.template_id === firstCard.template_id || c.id === firstCard.id);
      setSelectedCard(match || firstCard);
      if (onFirstCardShown) onFirstCardShown();
      // Silently refresh to get full card data from server
      loadCards(true);
    }
  }, [firstCard]);


  const loadCards = async (silent = false) => {
    if (!user?.id) {
      setLoading(false);
      return;
    }
    
    if (!silent) setLoading(true);
    const seqAtStart = cardsMutSeqRef.current;
    try {
      // Fan out `/users/me` alongside `/cards/me` so the global axios
      // interceptor catches the freshest `user.coins` from the user
      // payload. /cards/me alone doesn't carry coins, so without this
      // the Gallery page silently drifts out of sync with screens that
      // do hit /users/me (Earn, Profile). Both calls run in parallel
      // and share the same backend-side `apply_partial_exhibit_commit`
      // step, which is idempotent within the same second.
      const [userCardsRes, statsRes] = await Promise.all([
        api.get(`/cards/me`),
        api.get('/cards/stats'),
        api.get('/users/me').catch(() => null),
      ]);
      // Stale-response guard: if the player exhibited/repaired/returned
      // a card WHILE this request was in flight, this payload predates
      // that action — applying it would flip the card back to its
      // pre-action state in the UI while the server has the new state
      // (root cause of "repair says exhibiting on an idle-looking
      // card" for big collections, where /cards/me is slowest). Drop
      // it and re-fetch fresh data instead.
      if (cardsMutSeqRef.current !== seqAtStart) {
        setTimeout(() => loadCards(true), 400);
        return null;
      }
      setUserCards(userCardsRes.data);
      setHasAvailableCards(statsRes.data.available_count > 0);
      // Persist for the next tab re-visit. sessionStorage so it dies
      // on full reload (auth-bound staleness window).
      try {
        if (galleryCacheKey) {
          sessionStorage.setItem(galleryCacheKey, JSON.stringify({
            cards: userCardsRes.data,
            hasAvailable: statsRes.data.available_count > 0,
          }));
        }
      } catch { /* quota / private-mode — skip cache */ }
      return userCardsRes.data;
    } catch (err) {
      console.error('Error loading cards:', err);
      return null;
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // Keep the session snapshot in sync with what is actually on screen.
  // Optimistic changes (exhibit, repair) only touched local state, so the
  // stored snapshot kept the pre-action payload — re-entering the tab
  // rendered paintings as "not exhibiting" for a second until the fresh
  // /cards/me landed, which looked like a bug.
  useEffect(() => {
    if (!galleryCacheKey || !userCards.length) return;
    try {
      sessionStorage.setItem(galleryCacheKey, JSON.stringify({
        cards: userCards,
        hasAvailable: hasAvailableCards,
      }));
    } catch { /* quota / private-mode — skip cache */ }
  }, [galleryCacheKey, userCards, hasAvailableCards]);


  // Load cards when user changes or when navigating to this page.
  // `silent` skips the full-screen spinner whenever we already have
  // something to render (firstCard reveal, boot prefetch, OR
  // sessionStorage cache from a previous visit) — the refresh still
  // runs in the background so authoritative state lands within ~300ms.
  useEffect(() => {
    if (user?.id) {
      const haveSomething = !!firstCard || !!bootCards || !!galleryCached;
      loadCards(haveSomething);
    }
  }, [user?.id, location.pathname]);

  const buyCost = user?.next_card_cost || 100;

  const handleBuyRandom = () => {
    if (!user?.id || buying || showReel) return;
    const tutBuy = tutorialStep === 'buy_painting';
    haptic();
    if (tutBuy) setTutorialStep(null);
    setReelTutorial(tutBuy);
    setReelPurchasedCard(null);
    // Open the reel even if coins are insufficient — the reel shows
    // a clear "Need X more coins" state, which is way more helpful
    // than a dead button.
    setShowReel(true);
  };

  const handleReelPurchased = (card) => {
    setReelPurchasedCard(card);
  };

  const handleReelClose = async () => {
    setShowReel(false);
    if (reelPurchasedCard) {
      await loadCards(true);
      try { await refreshUser(); } catch (_) { /* noop */ }
      setSelectedCard(reelPurchasedCard);
      setReelPurchasedCard(null);
    }
  };

  const handleCardRevealComplete = async () => {
    const receivedCard = newCard;
    setNewCard(null);
    setBuying(false);
    // Wait for the gallery to reload so we can pick the freshly-
    // minted card off the server (with its real durability,
    // influence_xp, rarity, etc.) — using the preview-card payload
    // here would otherwise show durability=100 on the new player's
    // first painting and disable the tutorial's Repair step.
    const fresh = await loadCards(true);
    if (receivedCard) {
      const real = (Array.isArray(fresh) ? fresh : []).find(
        c => c.id === receivedCard.id || c.template_id === (receivedCard.template_id || receivedCard.id)
      );
      setSelectedCard(real || receivedCard);
    }
  };

  // Monotonic counter of local card mutations (optimistic updates +
  // authoritative single-card patches). `loadCards` captures it before
  // fetching and discards the response if it changed mid-flight.
  const cardsMutSeqRef = useRef(0);

  const handleExhibit = async (cardId) => {
    if (haptic) haptic();
    setExhibiting(cardId);
    cardsMutSeqRef.current += 1;
    // Optimistic local update — flip the card to "exhibiting" state in
    // the UI right now without waiting for the server. The progress
    // bar and overlay render off these fields, so the player sees
    // an instant response to their tap. The real server values
    // (precise expected_reward, exact end timestamp, etc.) replace
    // the optimistic shape when /cards/me returns; differences are
    // sub-100ms and the visual shape is identical.
    const card = userCards.find(c => c.id === cardId);
    const isTutorial = tutorialStep === 'exhibit';
    let snapshot = null;
    if (card) {
      snapshot = { ...card };
      // Backend tutorial reward = card.rating × BASE_EXHIBIT_MINUTES
      // (120) × 0.06 × dur × rarity × income.  The actual wait is
      // only 45 seconds though — so the timer end uses real elapsed
      // seconds while the reward calc uses the full 2-hour equivalent
      // band.  Pre-fix this mirror was using `0.75 minutes` for both,
      // which made the optimistic reward come out at ~1 coin for the
      // tutorial Rare Lv.19 (vs the 150-ish coins the backend
      // actually credits) — players saw the wrong "tiny payout" hint
      // and thought the bumped tutorial card was broken.
      const timerSeconds = isTutorial
        ? 45
        : (120 + ((user?.upgrades?.exhibit_duration || 0) * 10)) * 60;
      const rewardMinutes = isTutorial
        ? 120 // BASE_EXHIBIT_MINUTES — must match backend
        : 120 + ((user?.upgrades?.exhibit_duration || 0) * 10);
      const startMs = Date.now();
      const endMs = startMs + timerSeconds * 1000;
      const dur = calcLiveDurability(card);
      const rarityBonus = 1 + (RARITY_EXHIBIT_BONUS[card.rarity] || 0);
      const incomeBonus = 1 + (user?.upgrades?.exhibit_income || 0) * 0.01;
      // Match backend `EXHIBIT_COIN_MULT_PER_MINUTE` (0.06) so the
      // optimistic reward number doesn't change once the server
      // confirms.
      const expected = Math.round(card.rating * rewardMinutes * 0.06 * (dur / 100) * rarityBonus * incomeBonus);
      setUserCards(list => list.map(c => c.id === cardId ? {
        ...c,
        exhibit_start: new Date(startMs).toISOString(),
        exhibit_ends: new Date(endMs).toISOString(),
        exhibit_duration: rewardMinutes / 60,
        exhibit_duration_minutes: rewardMinutes,
        exhibit_start_durability: dur,
        exhibit_expected_reward: expected,
        exhibit_expected_decay: 0,  // negligible during animation
        exhibit_xp_reward: Math.max(2, Math.round(rewardMinutes / 30 * 8)),
      } : c));
    }
    try {
      const tutorialParam = isTutorial ? '?tutorial=true' : '';
      const res = await api.post(`/cards/${cardId}/exhibit${tutorialParam}`);
      // Patch in the server's authoritative values for this one card
      // instead of re-fetching the whole collection (which causes a
      // second perceived "loading" beat).
      if (res.data?.card) {
        cardsMutSeqRef.current += 1;
        setUserCards(list => list.map(c => c.id === cardId ? res.data.card : c));
      }
      if (isTutorial && setTutorialStep) {
        setTutorialStep('earn');
      }
    } catch (err) {
      // Roll back the optimistic update so the card returns to idle.
      if (snapshot) {
        setUserCards(list => list.map(c => c.id === cardId ? snapshot : c));
      }
      alert(err.response?.data?.detail || 'Error');
    } finally {
      setExhibiting(null);
    }
  };

  const handleReturnFromExhibit = async (cardId) => {
    if (haptic) haptic();
    setExhibiting(cardId);
    cardsMutSeqRef.current += 1;
    // Optimistic: strip exhibit fields locally immediately so the
    // card flips back to idle without waiting on the network.
    const card = userCards.find(c => c.id === cardId);
    let snapshot = null;
    if (card) {
      snapshot = { ...card };
      setUserCards(list => list.map(c => {
        if (c.id !== cardId) return c;
        const { exhibit_ends, exhibit_start, exhibit_duration,
                exhibit_duration_minutes, exhibit_start_durability,
                exhibit_expected_reward, exhibit_expected_decay,
                exhibit_xp_reward, ...rest } = c;
        return rest;
      }));
    }
    try {
      const res = await api.post(`/cards/${cardId}/return-from-exhibit`);
      if (res.data?.card) {
        cardsMutSeqRef.current += 1;
        setUserCards(list => list.map(c => c.id === cardId ? res.data.card : c));
      }
    } catch (err) {
      if (snapshot) {
        setUserCards(list => list.map(c => c.id === cardId ? snapshot : c));
      }
      alert(err.response?.data?.detail || 'Error');
    } finally {
      setExhibiting(null);
    }
  };

  const handleCollectExhibit = async (cardId) => {
    if (haptic) haptic();
    setCollecting(cardId);
    try {
      const res = await api.post(`/cards/${cardId}/collect-exhibit`);
      await refreshUser();
      await loadCards(true);
      triggerCoinReward(res.data.coins_earned);
      if (res.data.xp_gained > 0) {
        triggerInfluenceReward(res.data.xp_gained);
      }
      if (res.data.rarity_upgraded) {
        const label = RARITY_LABELS[res.data.new_rarity]?.[language] || RARITY_LABELS[res.data.new_rarity]?.en || res.data.new_rarity;
        setTimeout(() => {
          alert(`${t('level_upgraded')}: ${label}!`);
        }, 600);
      }
    } catch (err) {
      alert(err.response?.data?.detail || 'Error');
    } finally {
      setCollecting(null);
    }
  };

  // Inline repair from the gallery strip. Mirrors `handleRepair` in the
  // detail modal but operates on any card without opening it.
  // - Optimistically snaps `durability` to 100 so the strip's progress
  //   ring updates instantly.
  // - Calls /repair which charges coins; on failure rolls back and
  //   surfaces the error.
  const [quickRepairing, setQuickRepairing] = useState(null);
  const handleQuickRepair = async (card) => {
    if (!card || quickRepairing) return;
    // Defensive: skip if durability is essentially full. The button is
    // already disabled at >=99 in the disabled-prop, but a stale render
    // (e.g. tap fired right as the live decay tick rolled the value
    // down past the threshold) could still call here. Returning early
    // avoids the "Painting is already at 100% condition" backend error
    // that confused players during onboarding.
    if (calcLiveDurability(card) >= 99) return;
    if (haptic) haptic();
    setQuickRepairing(card.id);
    cardsMutSeqRef.current += 1;
    const prev = card;
    const nowIso = new Date().toISOString();
    setUserCards((list) => list.map((c) => (
      c.id === card.id ? { ...c, durability: 100, durability_updated_at: nowIso } : c
    )));
    try {
      const res = await api.post(`/cards/${card.id}/repair`);
      if (res.data?.card) {
        cardsMutSeqRef.current += 1;
        setUserCards((list) => list.map((c) => (c.id === card.id ? res.data.card : c)));
      }
      if (res.data?.user) setUser(res.data.user);
      else if (typeof res.data?.coins_remaining === 'number') {
        setUser((u) => (u ? { ...u, coins: res.data.coins_remaining } : u));
      } else {
        await refreshUser();
      }
    } catch (err) {
      setUserCards((list) => list.map((c) => (c.id === card.id ? prev : c)));
      const msg = err.response?.data?.detail;
      if (msg === 'Not enough coins') {
        alert(t('not_enough_coins') || 'Not enough coins');
      } else {
        alert(msg || t('error') || 'Error');
      }
    } finally {
      setQuickRepairing(null);
    }
  };

  // Repair All / Exhibit All — run card by card so the player sees the
  // gallery change immediately instead of waiting for one long request.
  const [bulkOp, setBulkOp] = useState(null); // { kind, done, total }
  const bulkAbortRef = useRef(false);
  useEffect(() => () => { bulkAbortRef.current = true; }, []);
  // Local wrapper that injects the player's `durability_slow` level.
  const calcLiveDurability = (card) => computeLiveDurability(card, user?.upgrades?.durability_slow);
  const payoutScore = (card) => (card.rating || 1)
    * Math.max(1, calcLiveDurability(card))
    * (1 + (RARITY_EXHIBIT_BONUS[card.rarity] || 0));

  const repairableCount = useMemo(() => userCards.filter(c =>
    !c.exhibit_ends && calcLiveDurability(c) < 99
  ).length, [userCards]); // eslint-disable-line react-hooks/exhaustive-deps

  const exhibitCandidates = useMemo(() => {
    const now = Date.now();
    const active = userCards.filter(c => c.exhibit_ends && new Date(c.exhibit_ends).getTime() > now).length;
    const free = Math.max(0, (user?.max_exhibits || 1) - active);
    // Expected payout order: level × condition × rarity bonus (the same
    // factors the server multiplies when the exhibit starts).
    const list = userCards
      .filter((c) => !c.exhibit_ends && calcLiveDurability(c) > 0 && !c.on_sale)
      .sort((a, b) => payoutScore(b) - payoutScore(a));
    return { list, free };
  }, [userCards, user?.max_exhibits]); // eslint-disable-line react-hooks/exhaustive-deps

  const exhibitableCount = Math.min(exhibitCandidates.list.length, exhibitCandidates.free);

  // Repair All is a single server call (it picks the most damaged
  // paintings it can afford and charges once) — the progress bar just
  // animates through the result so the player still sees it happen.
  const handleRepairAll = async () => {
    if (bulkOp) return;
    if (haptic) haptic();
    bulkAbortRef.current = false;
    const total = repairableCount;
    setBulkOp({ kind: 'repair', done: 0, total });
    cardsMutSeqRef.current += 1;
    const { toast } = await import('sonner');
    try {
      const { data } = await api.post('/cards/repair-all');
      const ids = data?.repaired_ids || [];
      if (typeof data?.coins_remaining === 'number') {
        setUser((u) => (u ? { ...u, coins: data.coins_remaining } : u));
      }
      const stamp = new Date().toISOString();
      for (let i = 0; i < ids.length; i += 1) {
        if (bulkAbortRef.current) break;
        const id = ids[i];
        setUserCards((prev) => prev.map((c) => (
          c.id === id ? { ...c, durability: 100, durability_updated_at: stamp } : c
        )));
        setBulkOp((op) => (op ? { ...op, done: i + 1 } : op));
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, Math.min(90, 500 / Math.max(1, ids.length))));
      }
      const skipped = Math.max(0, total - ids.length);
      toast.success(t('bulk_repaired')
        .replace('{n}', ids.length)
        .replace('{spent}', (data?.coins_spent || 0).toLocaleString()));
      if (skipped > 0) toast(t('bulk_skipped').replace('{n}', skipped));
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      toast.error(detail === 'Nothing to repair' ? t('bulk_no_coins') : (detail || t('bulk_no_coins')));
    } finally {
      cardsMutSeqRef.current += 1;
      setBulkOp(null);
      if (!bulkAbortRef.current) loadCards(true);
    }
  };

  // Exhibit All mirrors Repair All: one server call starts every free
  // slot (highest income first) and the progress bar animates through
  // the result — firing one request per painting was the slow part.
  const handleExhibitAll = async () => {
    if (bulkOp) return;
    if (haptic) haptic();
    bulkAbortRef.current = false;
    const total = Math.min(exhibitCandidates.list.length, exhibitCandidates.free);
    setBulkOp({ kind: 'exhibit', done: 0, total });
    cardsMutSeqRef.current += 1;
    const { toast } = await import('sonner');
    try {
      const { data } = await api.post('/cards/exhibit-all');
      const fresh = data?.cards || [];
      // The server may start fewer than the client predicted (a slot got
      // taken meanwhile) — retarget the bar so it always reaches N/N.
      setBulkOp((op) => (op ? { ...op, total: fresh.length || op.total } : op));
      for (let i = 0; i < fresh.length; i += 1) {
        if (bulkAbortRef.current) break;
        const c = fresh[i];
        setUserCards((prev) => prev.map((x) => (x.id === c.id ? { ...x, ...c } : x)));
        setBulkOp((op) => (op ? { ...op, done: i + 1 } : op));
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, Math.min(90, 500 / Math.max(1, fresh.length))));
      }
      toast.success(t('bulk_exhibited').replace('{n}', data?.exhibited_count || 0));
      if (total > (data?.exhibited_count || 0)) toast(t('bulk_slots_full'));
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      toast.error(
        detail.includes('slots used') || detail === 'Nothing to exhibit'
          ? t('bulk_slots_full')
          : (detail || t('bulk_slots_full'))
      );
    } finally {
      cardsMutSeqRef.current += 1;
      setBulkOp(null);
      if (!bulkAbortRef.current) loadCards(true);
    }
  };

  // Heartbeat for slow UI refreshers (idle durability bars). Exhibit
  // countdowns are now self-ticking inside <ExhibitTimer /> and the
  // live coin drip renders inside <LiveCoinPill />, so the page-level
  // heartbeat only needs a 60s cadence — the 1Hz full-page re-render
  // (50+ tiles/sec for big collections) is gone.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => {
      setTick(t => (t + 1) | 0);
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  // Refresh cards right when the earliest running exhibit finishes so
  // the "Done" overlay clears and coins/XP commit without waiting for
  // the next manual interaction. If an exhibit is already past its end
  // on the CLIENT clock but the server hasn't committed yet (clock
  // skew — server time slightly behind the device), keep polling every
  // 3s until the server clears `exhibit_ends`.
  useEffect(() => {
    const now = Date.now();
    const ends = userCards
      .filter(c => c.exhibit_ends)
      .map(c => new Date(c.exhibit_ends).getTime());
    if (ends.length === 0) return;
    const future = ends.filter(ts => ts > now);
    const delay = future.length
      ? Math.max(1200, Math.min(...future) - now + 1200)
      : 3000;
    const id = setTimeout(() => {
      loadCards(true);
      refreshUser?.();
    }, delay);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userCards]);

  // Live coin balance renders via <LiveCoinPill /> (external store).
  // We still publish the cards' exhibit state up to context whenever
  // the local list changes; the context drives the drip from there.
  useEffect(() => {
    publishActiveExhibits(userCards);
  }, [userCards, publishActiveExhibits]);

  // Watch for newly-finished exhibits and credit the player visually +
  // refresh server state so the durability/exhibit fields are cleared.
  // Backend `/cards/me` auto-credits coins+XP via
  // `apply_pending_exhibit_commit`, but the client also needs to:
  //   1) flash the floating coin/XP animations so the player notices
  //   2) emit a one-shot toast "Earnings collected — start a new cycle"
  //      so retention isn't lost when Collect buttons go away.
  // We track which exhibits we've already toasted in a ref so this
  // never fires twice for the same batch.
  const autoCollectSeenRef = useRef(new Set());
  const autoCollectBatchTimerRef = useRef(null);
  useEffect(() => {
    if (!userCards || userCards.length === 0) return;
    const now = Date.now();
    const newlyFinished = userCards.filter(c =>
      c.exhibit_ends &&
      new Date(c.exhibit_ends).getTime() <= now &&
      !autoCollectSeenRef.current.has(c.id)
    );
    if (newlyFinished.length === 0) return;
    let earnedCoins = 0;
    let earnedXp = 0;
    for (const c of newlyFinished) {
      autoCollectSeenRef.current.add(c.id);
      // Only flash the un-drip-credited remainder — the drip portion
      // already produced its own +N flashes while the exhibit was
      // running, so flashing the *full* `exhibit_expected_reward`
      // here would visually double-count.
      const expected = c.exhibit_expected_reward || 0;
      const paid = c.exhibit_coins_paid || 0;
      earnedCoins += Math.max(0, expected - paid);
      earnedXp += c.exhibit_xp_reward || 0;
    }
    if (earnedCoins > 0) triggerCoinReward(earnedCoins);
    if (earnedXp > 0) triggerInfluenceReward(earnedXp);
    // Debounce: when multiple cards mature in the same render pass, we
    // batch them into a single follow-up `loadCards` call so the cards
    // re-fetch is done once. The server-side commit is idempotent.
    if (autoCollectBatchTimerRef.current) clearTimeout(autoCollectBatchTimerRef.current);
    autoCollectBatchTimerRef.current = setTimeout(() => {
      loadCards(true);
      refreshUser?.();
    }, 600);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userCards]);


  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#05050A]">
        <SectionLoader progress={0} language={language} />
      </div>
    );
  }

  // Show card list if user has cards
  if (userCards.length > 0) {
    return (
      <div className="pb-24 px-4">
        {/* Header — sticky */}
        <div className="sticky top-0 z-40 bg-[#05050A] pt-4 pb-2 -mx-4 px-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <GameTooltip text={language === 'ru' ? 'Ваши монеты' : 'Your coins'} position="bottom">
                <LiveCoinPill />
              </GameTooltip>
              <GameTooltip text={t('total_influence')} position="bottom">
                <div className="relative">
                  <div className={`flex items-center gap-1.5 glass-card px-2.5 py-1.5 rounded-full cursor-pointer transition-all duration-300 ${
                    influencePulse ? 'scale-110 ring-2 ring-[#D4AF37]/60 bg-[#D4AF37]/15' : ''
                  }`}>
                    <ChessRook className={`w-3.5 h-3.5 text-[#D4AF37] transition-transform duration-300 ${influencePulse ? 'scale-125' : ''}`} />
                    <span className="font-mono text-xs tabular-nums">{user?.total_rating || 0}</span>
                  </div>
                  {floatingInfluence && (
                    <div className="absolute -top-1 left-1/2 -translate-x-1/2 pointer-events-none animate-coin-float z-50">
                      <span className="text-sm font-bold text-[#D4AF37] drop-shadow-lg">+{floatingInfluence}</span>
                    </div>
                  )}
                </div>
              </GameTooltip>
              <GameTooltip text={t('painting_slots')} position="bottom">
                <div className="flex items-center gap-1.5 glass-card px-2.5 py-1.5 rounded-full cursor-pointer">
                  <Image className="w-3.5 h-3.5 text-[#A1A1AA]" />
                  <span className="font-mono text-xs tabular-nums">{userCards.length}/{user?.max_paintings || 1}</span>
                </div>
              </GameTooltip>
              {/* Collection value pill — temporarily hidden by user request.
                  Reach me on chat to bring it back if needed. */}
            </div>
            <button
              data-testid="profile-btn"
              onClick={() => { haptic(); navigate('/profile'); }}
              className="w-8 h-8 rounded-full glass-card border border-white/10 flex items-center justify-center hover:border-[#D4AF37]/50 transition-colors"
            >
              {user?.photo_url
                ? <img src={user.photo_url} alt="" className="w-8 h-8 rounded-full object-cover" />
                : <User className="w-4 h-4 text-[#A1A1AA]" />
              }
            </button>
          </div>

          {/* Paintings / Collections switch — collections are a showcase
              layer, so the paintings-specific actions (buy, repair all,
              sort, grid) hide on the collections tab. */}
          <div className="flex gap-1 p-1 rounded-xl bg-[#0a0a12] mb-3" data-testid="gallery-tabs">
            {[
              { id: 'paintings', ru: 'Картины', en: 'Paintings' },
              { id: 'collections', ru: 'Коллекции', en: 'Collections' },
            ].map((tabDef) => (
              <button
                key={tabDef.id}
                data-testid={`gallery-tab-${tabDef.id}`}
                onClick={() => { haptic(); setGalleryTab(tabDef.id); }}
                className={`flex-1 py-1.5 px-2 rounded-lg text-[11px] font-medium transition-all flex items-center justify-center gap-1 ${
                  galleryTab === tabDef.id
                    ? 'bg-[#1a1a2e] text-[#F5F5F7] shadow-sm'
                    : 'text-[#A1A1AA] hover:text-[#F5F5F7]'
                }`}
              >
                {tabDef.id === 'collections'
                  ? <Layers className="w-3.5 h-3.5" />
                  : <Image className="w-3.5 h-3.5" />}
                {language === 'ru' ? tabDef.ru : tabDef.en}
              </button>
            ))}
          </div>

          {/* Buy Button */}
          {galleryTab === 'paintings' && hasAvailableCards && userCards.length < (user?.max_paintings || 1) && (
            <button
              data-testid="buy-random-btn"
              onClick={handleBuyRandom}
              disabled={buying || showReel || (tutorialStep && tutorialStep !== 'buy_painting')}
              className={`w-full py-3 rounded-xl transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed mb-3 relative ${
                tutorialStep === 'buy_painting' 
                  ? 'bg-gradient-to-r from-[#D4AF37] to-[#F5D07A] text-[#0C0C14] border border-[#D4AF37] shadow-[0_0_16px_rgba(212,175,55,0.55)] z-50' 
                  : (user?.coins || 0) >= buyCost
                  ? 'bg-gradient-to-r from-[#D4AF37] to-[#F5D07A] text-[#0C0C14] border border-[#D4AF37]/60 hover:shadow-[0_0_12px_rgba(212,175,55,0.5)] active:scale-95'
                  : 'glass-card border border-[#D4AF37]/30 hover:border-[#D4AF37]'
              }`}
            >
              {(() => {
              const canAfford = (user?.coins || 0) >= buyCost;
              const isTutorial = tutorialStep === 'buy_painting';
              return (
              <>
              {isTutorial && (
                <>
                  <span className="tut-ring" aria-hidden="true" />
                  <span className="tut-ring tut-ring-delayed" aria-hidden="true" />
                  <TutorialDialog
                    text={t('tut_buy')}
                    position="bottom"
                  />
                </>
              )}
              <Gift className={`w-4 h-4 ${canAfford || isTutorial ? 'text-[#0C0C14]' : 'text-[#D4AF37]'}`} />
              <span className={`text-sm font-bold ${canAfford || isTutorial ? 'text-[#0C0C14]' : 'text-[#F5F5F7]'}`}>{t('buy_random_card')}</span>
              </>
              );
            })()}
            </button>
          )}
          {galleryTab === 'paintings' && userCards.length >= (user?.max_paintings || 2) && (
            <div className="w-full py-2.5 rounded-xl glass-card border border-[#52525B]/30 text-center mb-4">
              <span className="text-xs text-[#A1A1AA]">
                {t('slots_full')}
              </span>
            </div>
          )}
          {galleryTab === 'paintings' && !tutorialStep && userCards.length > 10 &&
            (repairableCount >= 2 || exhibitableCount >= 1 || bulkOp) && (
            <div className="mb-4">
              <div className="flex items-stretch gap-2">
                {exhibitableCount >= 1 && exhibitCandidates.list.length >= 2 && (
                  <button
                    data-testid="exhibit-all-btn"
                    onClick={handleExhibitAll}
                    disabled={!!bulkOp}
                    className="group relative flex-1 overflow-hidden rounded-2xl px-3 py-2.5 text-left border border-[#D4AF37]/25 bg-[linear-gradient(135deg,rgba(212,175,55,0.18),rgba(212,175,55,0.03))] shadow-[inset_0_1px_0_0_rgba(255,255,255,0.07),0_10px_26px_-16px_rgba(212,175,55,0.9)] transition-[border-color,box-shadow,transform] duration-200 active:scale-[0.97] hover:border-[#D4AF37]/55 disabled:opacity-50"
                  >
                    <span
                      aria-hidden="true"
                      className="pointer-events-none absolute inset-y-0 -left-full w-1/2 bg-gradient-to-r from-transparent via-white/20 to-transparent transition-transform duration-700 group-hover:translate-x-[300%] group-active:translate-x-[300%]"
                    />
                    <span className="relative flex items-center gap-2.5">
                      <span className="w-8 h-8 shrink-0 rounded-xl bg-[#D4AF37]/15 border border-[#D4AF37]/25 flex items-center justify-center">
                        <Coins className="w-4 h-4 text-[#D4AF37]" />
                      </span>
                      <span className="flex flex-col leading-tight min-w-0">
                        <span className="text-[9px] uppercase tracking-[0.16em] text-[#D4AF37]/75 truncate">
                          {t('bulk_exhibit_label')}
                        </span>
                        <span className="text-sm font-semibold text-[#F5F5F7]">
                          {t('bulk_all')} · <span className="font-mono text-[#D4AF37]">{exhibitableCount}</span>
                        </span>
                      </span>
                    </span>
                  </button>
                )}
                {repairableCount >= 2 && (
                  <button
                    data-testid="repair-all-btn"
                    onClick={handleRepairAll}
                    disabled={!!bulkOp}
                    className="group relative flex-1 overflow-hidden rounded-2xl px-3 py-2.5 text-left border border-[#F59E0B]/25 bg-[linear-gradient(135deg,rgba(245,158,11,0.18),rgba(245,158,11,0.03))] shadow-[inset_0_1px_0_0_rgba(255,255,255,0.07),0_10px_26px_-16px_rgba(245,158,11,0.9)] transition-[border-color,box-shadow,transform] duration-200 active:scale-[0.97] hover:border-[#F59E0B]/55 disabled:opacity-50"
                  >
                    <span
                      aria-hidden="true"
                      className="pointer-events-none absolute inset-y-0 -left-full w-1/2 bg-gradient-to-r from-transparent via-white/20 to-transparent transition-transform duration-700 group-hover:translate-x-[300%] group-active:translate-x-[300%]"
                    />
                    <span className="relative flex items-center gap-2.5">
                      <span className="w-8 h-8 shrink-0 rounded-xl bg-[#F59E0B]/15 border border-[#F59E0B]/25 flex items-center justify-center">
                        <Wrench className="w-4 h-4 text-[#F59E0B]" />
                      </span>
                      <span className="flex flex-col leading-tight min-w-0">
                        <span className="text-[9px] uppercase tracking-[0.16em] text-[#F59E0B]/75 truncate">
                          {t('bulk_repair_label')}
                        </span>
                        <span className="text-sm font-semibold text-[#F5F5F7]">
                          {t('bulk_all')} · <span className="font-mono text-[#F59E0B]">{repairableCount}</span>
                        </span>
                      </span>
                    </span>
                  </button>
                )}
              </div>
              {bulkOp && (
                <div data-testid="bulk-progress" className="mt-2">
                  <div className="h-1.5 rounded-full bg-white/8 overflow-hidden">
                    <div
                      className="h-full rounded-full transition-[width] duration-200"
                      style={{
                        width: `${Math.max(6, Math.round((bulkOp.done / Math.max(1, bulkOp.total)) * 100))}%`,
                        background: bulkOp.kind === 'repair'
                          ? 'linear-gradient(90deg, #F59E0B, #FCD34D)'
                          : 'linear-gradient(90deg, #D4AF37, #F5D07A)',
                      }}
                    />
                  </div>
                  <p className="text-[10px] text-[#A1A1AA] mt-1 text-center font-mono">
                    {bulkOp.kind === 'repair' ? t('bulk_repairing') : t('bulk_exhibiting')} {bulkOp.done}/{bulkOp.total}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Team panel — temporarily hidden on the client while we
            redesign where it lives. The backend mechanics (Restorer,
            PR Manager) keep running for any player whose contract
            is already active; we only suppress the UI entry point so
            new users don't see/interact with it for now. Re-enable by
            removing the `false &&` guard. */}
        {false && userCards.length >= 2 && (
          <TeamPanel
            hiredUntil={hiredUntil}
            onSlotClick={() => {
              haptic();
              // Gate 1: collection size — first 5 paintings are an
              // explicit early-game goal. Below that, we just nudge.
              if (userCards.length < 5) {
                import('sonner').then(({ toast }) => toast(
                  language === 'ru'
                    ? `Нужно минимум 5 картин в коллекции (сейчас ${userCards.length}/5)`
                    : `Need 5+ paintings to hire help (you have ${userCards.length}/5)`,
                  { icon: '🎯' }
                ));
                return;
              }
              // Gate 2: nothing to repair — exhibited cards are skipped
              // by the Restorer, so check there's at least one damaged
              // non-exhibited card. Otherwise hiring would do nothing.
              const now = new Date();
              const hasWork = userCards.some((c) => {
                const onExhibit = c.exhibit_ends && new Date(c.exhibit_ends) > now;
                if (onExhibit) return false;
                return (c.durability ?? 100) < 100;
              });
              if (!hasWork) {
                import('sonner').then(({ toast }) => toast(
                  language === 'ru'
                    ? 'Все картины в порядке — нечего ремонтировать'
                    : 'All paintings are fine — nothing to repair',
                  { icon: '✨' }
                ));
                return;
              }
              setHireSheetOpen(true);
            }}
            restorerQueue={restorerQueue}
            restorerName={restorerName}
            restorerBlocked={!!user?.team_restorer_blocked}
            language={language}
            prManagerUnlocked={prManagerUnlocked}
            prManagerName={prManagerName}
            prManagerLevel={prManagerLevel}
            prManagerSlotsUsed={prManagerSlotsUsed}
            prManagerSlotsTotal={prManagerSlotsTotal}
            prAssignments={prState?.assignments || []}
            onPrManagerClick={() => {
              haptic();
              setPrSheetOpen(true);
            }}
          />
        )}

        {/* Collect-all is gone — exhibits auto-credit coins+XP the
            moment they finish (see backend `apply_pending_exhibit_commit`).
            The floating "+N coins" animation triggered by the
            useEffect on userCards already conveys the rewards. */}

        {/* Sort chips — only visible once collection is big enough that
            scroll-fatigue becomes real. Pure client-side sort, no API calls. */}
        {galleryTab === 'paintings' && userCards.length > SORT_THRESHOLD && (
          <div className="flex items-center gap-1.5 mb-2 overflow-x-auto pb-1" data-testid="gallery-sort-bar">
            <span className="text-[10px] text-[#A1A1AA] uppercase tracking-wider shrink-0">
              {language === 'ru' ? 'Сорт.' : 'Sort'}
            </span>
            {SORT_OPTIONS.map((opt) => {
              const active = sortBy === opt.id;
              return (
                <button
                  key={opt.id}
                  data-testid={`gallery-sort-${opt.id}${active ? '-active' : ''}`}
                  onClick={() => { haptic?.(); persistSort(opt.id); }}
                  className={`shrink-0 px-2.5 py-1 rounded-full text-[11px] font-semibold transition ${
                    active
                      ? 'bg-[#D4AF37] text-[#0C0C14]'
                      : 'bg-white/5 text-[#A1A1AA] border border-white/10 hover:bg-white/10'
                  }`}
                >
                  {language === 'ru' ? opt.label_ru : opt.label_en}
                </button>
              );
            })}
          </div>
        )}

        {/* Cards Grid */}
        {galleryTab === 'collections' ? (
          <CollectionsPanel />
        ) : (
        <div className="grid grid-cols-2 gap-3" data-testid="cards-grid">
          {sortedCards.map((card, idx) => {
            // Presence-based busy check: while the server still holds
            // `exhibit_ends` on the card it WILL reject repair/exhibit
            // actions — even if the client clock already passed the end
            // time (phones with clocks a few seconds ahead hit
            // "Painting is being exhibited" on a card that looked
            // idle). Treat the card as busy until the server commits
            // the exhibit and clears the field; the finish-watcher
            // below polls /cards/me until that happens.
            const isExhibiting = !!card.exhibit_ends;
            const isDone = card.exhibit_ends && new Date(card.exhibit_ends) <= new Date();
            const lvl = card.upgrade_level || 0;
            const tierColor = lvl >= 5 ? '#E5E4E2' : lvl >= 3 ? '#D4AF37' : lvl >= 1 ? '#CD7F32' : '';
            const tierGlow = lvl >= 5 
              ? '0 0 14px rgba(229,228,226,0.45)' 
              : lvl >= 3 
              ? `0 0 ${8 + lvl * 2}px rgba(212,175,55,0.4)` 
              : lvl >= 1 
              ? `0 0 ${6 + lvl * 2}px rgba(205,127,50,0.3)` 
              : '';
            const rarityColor = card.rarity !== 'common' ? RARITY_COLORS[card.rarity] : null;
            const hasTierBorder = lvl > 0;
            const rarityGlow = rarityColor && !hasTierBorder ? `0 0 16px ${rarityColor}50, 0 0 6px ${rarityColor}30` : '';
            
            return (
              <div
                key={card.id}
                className={`glass-card card-cv rounded-xl overflow-hidden relative ${hasTierBorder ? 'border' : ''}`}
                style={hasTierBorder
                  ? { borderColor: tierColor, boxShadow: tierGlow }
                  : rarityGlow ? { boxShadow: rarityGlow } : undefined
                }
              >
                {/* Clickable card area */}
                <div
                  role="button"
                  tabIndex={0}
                  data-testid={`card-${card.id}`}
                  onClick={() => { haptic(); setSelectedCard(card); }}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); haptic(); setSelectedCard(card); } }}
                  className="w-full text-left transition-transform active:scale-[0.97] cursor-pointer"
                >
                  <div className="aspect-[3/4] relative">
                    {/* Upgrade level badge */}
                    {lvl > 0 && (
                      <div 
                        className="absolute top-1.5 left-1.5 z-20 flex items-center gap-0.5 px-1.5 py-0.5 rounded-md"
                        style={{ 
                          background: `linear-gradient(135deg, ${tierColor}22, ${tierColor}44)`,
                          border: `1px solid ${tierColor}66`,
                          backdropFilter: 'blur(4px)'
                        }}
                      >
                        <ChevronRight className="w-2.5 h-2.5" style={{ transform: 'rotate(-90deg)', color: tierColor }} />
                        <span className="font-mono text-[9px] font-bold" style={{ color: tierColor }}>{lvl}</span>
                      </div>
                    )}
                    {isExhibiting && (
                      <div className="absolute inset-0 bg-black/30 z-10 flex flex-col items-center justify-center gap-2">
                        <FloatingRewards />
                        <div className="bg-black/60 rounded-full px-2 py-1 flex items-center gap-1">
                          <Clock className="w-3 h-3 text-[#D4AF37]" />
                          <span className="text-[10px] text-[#D4AF37] font-mono">
                            <ExhibitTimer endsAt={card.exhibit_ends} />
                          </span>
                        </div>
                        {/* Reward pills (+coins, +XP) intentionally removed
                            from the in-flight overlay — the live coin
                            counter at the top already shows earnings
                            ticking up, and the +XP pop fires at expire.
                            Keeping the overlay minimal makes the Return
                            button the obvious action. */}
                        <button
                          data-testid={`return-exhibit-${card.id}`}
                          onClick={(e) => { e.stopPropagation(); handleReturnFromExhibit(card.id); }}
                          disabled={exhibiting === card.id || tutorialStep === 'repair'}
                          className="px-2.5 py-1 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/50 text-[#D4AF37] text-[10px] font-medium flex items-center gap-1 transition-all active:scale-95 hover:bg-[#D4AF37]/25 hover:border-[#D4AF37]/80 disabled:opacity-30"
                          title={language === 'ru' ? 'Вернуть с выставки (без награды)' : 'Return early (no reward)'}
                        >
                          <span className="leading-none">⤺</span>
                          <span>{language === 'ru' ? 'Вернуть' : 'Return'}</span>
                        </button>
                      </div>
                    )}
                    {card.animation_url ? (
                      // Animated TON-card render. Uses the still image as
                      // `poster` so the card is visible instantly (already
                      // in browser cache), then the loop fades in once the
                      // browser has the video frame ready. `preload="metadata"`
                      // keeps the initial page weight low — full payload
                      // only kicks in for cards that are actually visible.
                      <video
                        src={card.animation_url}
                        poster={card.image_url}
                        autoPlay
                        loop
                        muted
                        playsInline
                        preload="metadata"
                        className={`w-full h-full object-cover ${isExhibiting ? 'opacity-60' : ''}`}
                        style={{
                          objectPosition: `${card.image_focus_x ?? 50}% ${card.image_focus_y ?? 50}%`,
                        }}
                      />
                    ) : (
                      <img
                        src={thumb(card.image_url, 320)}
                        alt={card.title}
                        loading="lazy"
                        decoding="async"
                        className={`w-full h-full object-cover ${isExhibiting ? 'opacity-60' : ''}`}
                        style={{
                          objectPosition: `${card.image_focus_x ?? 50}% ${card.image_focus_y ?? 50}%`,
                          transition: 'filter 0.7s',
                          ...(tutorialStep === 'repair' && (card.durability || 100) < 100
                            ? { filter: 'sepia(0.04) contrast(0.975) brightness(0.97) saturate(0.95)' }
                            : {}),
                        }}
                      />
                    )}
                    {/* Rarity sparkle halo for TON-minted cards */}
                    {card.nft_address && (
                      <TonSparkles rarity={card.rarity} sizeScale={0.7} />
                    )}
                    {/* Minted NFT badge — top-left corner */}
                    {card.nft_address && (
                      <div
                        data-testid={`nft-badge-${card.id}`}
                        title="Minted on TON"
                        className="absolute top-1.5 left-1.5 w-5 h-5 rounded-full flex items-center justify-center shadow-lg"
                        style={{
                          background: 'linear-gradient(135deg,#0098EA,#0073C7)',
                          boxShadow: '0 0 10px rgba(0,152,234,0.6)',
                        }}
                      >
                        <Check className="w-3 h-3 text-white" strokeWidth={3} />
                      </div>
                    )}
                    {/* UNIQUE badge — top-right corner */}
                    {card.is_unique && (
                      <div
                        data-testid={`unique-badge-${card.id}`}
                        title={language === 'ru' ? 'Единственный экземпляр' : 'One of a kind'}
                        className="absolute top-1.5 right-1.5 px-1.5 py-0.5 rounded-md text-[8px] font-bold tracking-wider"
                        style={{
                          background: 'linear-gradient(135deg,#FACC15,#F59E0B)',
                          color: '#0a0a12',
                          boxShadow: '0 0 8px rgba(250,204,21,0.5)',
                          letterSpacing: '0.08em',
                        }}
                      >
                        UNIQUE
                      </div>
                    )}
                    {/* ON SALE badge — top-left corner, shown while card
                        is listed in a player auction (escrow holds the NFT) */}
                    {card.on_sale && (
                      <div
                        data-testid={`on-sale-badge-${card.id}`}
                        title={language === 'ru' ? 'Выставлено на аукцион' : 'Listed on auction'}
                        className="absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded-md text-[8px] font-bold tracking-wider animate-pulse"
                        style={{
                          background: 'linear-gradient(135deg,#A855F7,#7C3AED)',
                          color: '#fff',
                          boxShadow: '0 0 10px rgba(168,85,247,0.6)',
                          letterSpacing: '0.08em',
                        }}
                      >
                        ON SALE
                      </div>
                    )}
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2 pt-6">
                      <div className="flex items-center justify-between mb-0.5">
                        <div className="flex items-center gap-1">
                          <ChessRook className="w-3 h-3 text-[#D4AF37] fill-[#D4AF37]" />
                          <span className="font-mono text-xs text-[#D4AF37]">{getInfluenceLevel(card.influence_xp || 0)}</span>
                        </div>
                        <div
                          className="flex items-center gap-0.5"
                          title={language === 'ru' ? 'Мин. ценность' : 'Min. value'}
                        >
                          <Star className="w-2.5 h-2.5 text-[#FACC15] fill-[#FACC15] animate-star-pulse" />
                          <span className="font-mono text-[10px] text-[#FACC15]">
                            {cardValue(card)}
                          </span>
                        </div>
                      </div>
                    </div>
                    {/* Rarity + condition bar (combined) */}
                    <div className="absolute bottom-0 left-0 right-0 h-1 bg-[#1a1a2e]">
                      <div
                        className="h-full transition-all duration-300"
                        style={{
                          width: `${calcLiveDurability(card)}%`,
                          backgroundColor: RARITY_COLORS[card.rarity] || '#9CA3AF',
                        }}
                      />
                      {/* Restorer-tick flash: thin green pulse over the bar
                          plus a floating "+N" near its right edge. Driven
                          by `repairFlashes[card.id]`, auto-clears after
                          ~2.2s. Pure decoration — no logic depends on it. */}
                      {repairFlashes[card.id] && (
                        <>
                          <div
                            key={`flash-bar-${repairFlashes[card.id].ts}`}
                            className="absolute inset-0 pointer-events-none"
                            style={{
                              background: 'linear-gradient(90deg, transparent 0%, rgba(52,211,153,0.85) 50%, transparent 100%)',
                              animation: 'restorer-bar-flash 1.4s ease-out forwards',
                            }}
                          />
                          <div
                            key={`flash-pop-${repairFlashes[card.id].ts}`}
                            className="absolute -top-4 right-1.5 pointer-events-none font-mono text-[10px] font-bold text-[#34D399]"
                            style={{
                              textShadow: '0 1px 4px rgba(0,0,0,0.85), 0 0 8px rgba(52,211,153,0.55)',
                              animation: 'restorer-pop 2.2s ease-out forwards',
                            }}
                          >
                            +{repairFlashes[card.id].delta.toFixed(1)}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="p-2.5 pb-1.5">
                    <div className="flex items-center justify-between mb-0.5">
                      <p className="text-xs font-medium text-[#F5F5F7] truncate flex-1">
                        {card.title} <span className="text-[10px] font-mono" style={{ color: RARITY_COLORS[card.rarity] || '#9CA3AF' }}>#{card.instance_number || ''}</span>
                      </p>
                      {calcLiveDurability(card) <= 50 && (
                        <Wrench className="w-3 h-3 text-[#EF4444] shrink-0 ml-1" />
                      )}
                    </div>
                    <p className="text-[10px] text-[#A1A1AA] truncate">{card.artist}</p>
                  </div>
                </div>

                {/* Exhibit + Repair buttons (split horizontally). The
                    Repair shortcut sits to the right of Exhibit so the
                    player can patch a damaged painting without having
                    to open the detail modal first. Both buttons use
                    icons only to keep the strip compact. Exhibit
                    button family (progress / collect / picker) takes
                    over the full width when relevant. */}
                {card.revealed && (
                  <div className="px-2.5 pb-2.5">
                    {isExhibiting ? (
                      <div data-testid={`exhibit-progress-${card.id}`}>
                        <ExhibitProgressBar endsAt={card.exhibit_ends} duration={card.exhibit_duration} startAt={card.exhibit_start} />
                        <p className="text-[9px] text-[#A1A1AA] text-center mt-1">
                          <ExhibitCityLabel t={t} />
                        </p>
                      </div>
                    ) : (
                      // `isDone` is intentionally NOT a separate branch
                      // any more — exhibits auto-credit coins/XP via the
                      // backend the moment their window expires. The
                      // card transitions straight back to the idle
                      // Exhibit/Repair pair without a Collect button.
                      <div className="flex gap-1.5">
                        <button
                          data-testid={`exhibit-btn-${card.id}`}
                          onClick={(e) => { e.stopPropagation(); handleExhibit(card.id); }}
                          disabled={exhibiting === card.id || collecting === card.id || tutorialStep === 'repair'}
                          className="flex-1 py-1.5 rounded-lg bg-[#141420] border border-[#D4AF37]/20 text-[#D4AF37] flex items-center justify-center gap-1 hover:border-[#D4AF37]/40 transition-all active:scale-95 disabled:opacity-30 disabled:cursor-not-allowed"
                          title={t('exhibit')}
                        >
                          <Coins className="w-3.5 h-3.5" />
                        </button>
                        <button
                          data-testid={`repair-quick-${card.id}`}
                          onClick={(e) => { e.stopPropagation(); handleQuickRepair(card); }}
                          disabled={
                            !!quickRepairing
                            || tutorialStep === 'repair'
                            || (calcLiveDurability(card) >= 99)
                          }
                          className="flex-1 py-1.5 rounded-lg bg-[#141420] border border-[#7DD3FC]/25 text-[#7DD3FC] flex items-center justify-center gap-1 hover:border-[#7DD3FC]/50 transition-all active:scale-95 disabled:opacity-30 disabled:cursor-not-allowed"
                          title={t('repair') || 'Repair'}
                        >
                          <Hammer className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        )}

        {/* Card Detail Modal */}
        {selectedCard && (
          <CardDetailModal
            card={selectedCard}
            onClose={() => setSelectedCard(null)}
            t={t}
            language={language}
            user={user}
            haptic={haptic}
            tutorialStep={tutorialStep}
            setTutorialStep={setTutorialStep}
            loadCards={loadCards}
            refreshUser={refreshUser}
            initialXpPreview={selectedCard ? getXpForCard(selectedCard.id) : {}}
            onUpgrade={(updatedCard) => {
              setSelectedCard(updatedCard);
              loadCards(true);
              refreshUser();
            }}
            onExhibitDone={(optimisticCard) => {
              // The modal closes immediately and passes an optimistic
              // snapshot of the now-on-exhibit card. We patch it into
              // the local list so the gallery shows the exhibition
              // state without waiting on the network. A background
              // `loadCards` reconciles the real server values.
              if (optimisticCard) {
                setUserCards(list => list.map(c =>
                  c.id === optimisticCard.id ? optimisticCard : c
                ));
              }
              loadCards(true);
              refreshUser();
            }}
          />
        )}

        {/* New Card Reveal Animation */}
        {newCard && (
          <CardRevealModal
            card={newCard}
            onComplete={handleCardRevealComplete}
            language={language}
          />
        )}

        {/* Wheel-of-fortune reel: handles purchase + reveal in one flow */}
        {showReel && (
          <RandomPaintingReel
            buyCost={buyCost}
            tutorial={reelTutorial}
            initialPool={reelPool}
            onPoolFetched={setReelPool}
            onPurchased={handleReelPurchased}
            onClose={handleReelClose}
          />
        )}

        {/* Restorer walker (mock) — fixed-positioned overlay */}
        <RestorerWalker
          active={restorerActive}
          cards={userCards}
          onQueue={setRestorerQueue}
          onCompleteVisit={async (cardId) => {
            try {
              const res = await api.post('/team/restorer/complete-visit', { card_id: cardId });
              const newDur = res.data?.durability;
              const newCoins = res.data?.user_coins;
              const blocked = !!res.data?.blocked;
              // Patch the local card cache so the walker bar + the
              // card-detail durability indicator reflect the new value
              // immediately (no waiting for the 30s poll).
              if (typeof newDur === 'number') {
                setUserCards((prev) =>
                  prev.map((c) =>
                    c.id === cardId
                      ? { ...c, durability: newDur, durability_updated_at: new Date().toISOString() }
                      : c
                  )
                );
              }
              if (typeof newCoins === 'number' || blocked) {
                setUser((u) => (u ? { ...u, coins: newCoins ?? u.coins, team_restorer_blocked: blocked || undefined } : u));
              }
            } catch (e) {
              // eslint-disable-next-line no-console
              console.warn('complete-visit failed', e);
            }
          }}
        />

        {/* Hire sheet — opens when player taps the Restorer slot. */}
        <RestorerHireSheet
          open={hireSheetOpen}
          language={language}
          coins={user?.coins || 0}
          hiredUntil={hiredUntil}
          restorerName={restorerName}
          onRequestRename={() => setRenameSheetOpen(true)}
          onClose={() => setHireSheetOpen(false)}
          onConfirm={async (shift) => {
            const { toast } = await import('sonner');
            // Map shift → label for toasts, kept aligned with backend.
            const labels = {
              hour:    language === 'ru' ? 'Смена 1 час активна'  : '1h shift activated',
              halfday: language === 'ru' ? 'Смена 12 часов активна' : '12h shift activated',
              day:     language === 'ru' ? 'Смена 24 часа активна' : '24h shift activated',
              month:   language === 'ru' ? 'Смена 30 дней активна' : '30d shift activated',
            };
            // Stars-paid path: open Telegram invoice, await user pay,
            // then call confirm to apply the hire (webhook is fallback).
            if (shift === 'month') {
              try {
                const inv = await api.post('/team/restorer/hire/invoice', { shift });
                const link = inv.data?.invoice_link;
                if (!link) throw new Error('No invoice link');
                const tg = window.Telegram?.WebApp;
                const status = await new Promise((resolve) => {
                  if (tg?.openInvoice) {
                    tg.openInvoice(link, (st) => resolve(st));
                  } else {
                    // Browser fallback (preview): open in new tab and
                    // optimistically treat as paid for QA.
                    window.open(link, '_blank');
                    setTimeout(() => resolve('paid'), 800);
                  }
                });
                if (status !== 'paid') {
                  if (status === 'cancelled') {
                    toast(language === 'ru' ? 'Оплата отменена' : 'Payment cancelled');
                  } else if (status === 'failed') {
                    toast.error(language === 'ru' ? 'Оплата не прошла' : 'Payment failed');
                  }
                  throw new Error(`status=${status}`);
                }
                const res = await api.post('/team/restorer/hire/confirm', { shift });
                if (res.data?.user) setUser(res.data.user);
                haptic && haptic('success');
                toast.success(labels[shift]);
              } catch (err) {
                if (!String(err?.message || '').startsWith('status=')) {
                  toast.error(language === 'ru' ? 'Не удалось оплатить' : 'Payment failed');
                }
                throw err;
              }
              return;
            }
            // Coins path
            try {
              const res = await api.post('/team/restorer/hire', { shift });
              const data = res.data;
              if (data?.user) {
                setUser(data.user);
              } else if (typeof data?.coins === 'number') {
                setUser((u) => u ? { ...u, coins: data.coins, team_restorer_hired_until: data.hired_until } : u);
              }
              haptic && haptic('success');
              toast.success(labels[shift] || (language === 'ru' ? 'Реставратор нанят' : 'Restorer hired'));
            } catch (err) {
              const msg = err?.response?.data?.detail || (language === 'ru' ? 'Не удалось нанять' : 'Hire failed');
              toast.error(msg);
              throw err;
            }
          }}
        />

        {/* Paid rename modal — opens when the player taps the pencil
            inside the hire sheet. Charges 1 ⭐ per rename. */}
        <RestorerRenameSheet
          open={renameSheetOpen}
          language={language}
          currentName={restorerName}
          onClose={() => setRenameSheetOpen(false)}
          onConfirm={async (newName) => {
            const { toast } = await import('sonner');
            try {
              const inv = await api.post('/team/restorer/rename/invoice', { name: newName });
              const link = inv.data?.invoice_link;
              if (!link) throw new Error('No invoice link');
              const tg = window.Telegram?.WebApp;
              const status = await new Promise((resolve) => {
                if (tg?.openInvoice) {
                  tg.openInvoice(link, (st) => resolve(st));
                } else {
                  window.open(link, '_blank');
                  setTimeout(() => resolve('paid'), 800);
                }
              });
              if (status !== 'paid') {
                if (status === 'cancelled') {
                  toast(language === 'ru' ? 'Оплата отменена' : 'Payment cancelled');
                } else if (status === 'failed') {
                  toast.error(language === 'ru' ? 'Оплата не прошла' : 'Payment failed');
                }
                throw new Error(`status=${status}`);
              }
              const res = await api.post('/team/restorer/rename/confirm', { name: newName });
              if (res.data?.user) setUser(res.data.user);
              try { localStorage.setItem('team_restorer_name', newName); } catch { /* ignore */ }
              haptic && haptic('success');
              toast.success(language === 'ru' ? `Имя изменено на ${newName}` : `Renamed to ${newName}`);
            } catch (err) {
              if (!String(err?.message || '').startsWith('status=')) {
                toast.error(language === 'ru' ? 'Не удалось сменить имя' : 'Rename failed');
              }
              throw err;
            }
          }}
        />

        {/* PR Manager (Molly) — main profile sheet */}
        <PRManagerSheet
          open={prSheetOpen}
          language={language}
          state={prState}
          userCoins={user?.coins || 0}
          onClose={() => setPrSheetOpen(false)}
          onRequestRename={() => setPrRenameOpen(true)}
          onRequestPicker={(excludeSet) => {
            // Returns a Promise that resolves with the picked card
            // (or null if the user closes the picker without choosing).
            return new Promise((resolve) => {
              prPickerResolverRef.current = resolve;
              prPickerExcludeRef.current = excludeSet || new Set();
              setPrPickerOpen(true);
            });
          }}
          onCommitAssignment={async (cardId) => {
            const { toast } = await import('sonner');
            try {
              const res = await api.post('/team/pr-manager/assign', { card_id: cardId });
              if (res.data?.user) setUser(res.data.user);
              await refreshPrState();
              haptic && haptic('success');
              toast.success(
                language === 'ru'
                  ? `Molly теперь сопровождает картину (+${res.data?.card_xp_award || 0} XP по возвращению)`
                  : `Molly is now managing the painting (+${res.data?.card_xp_award || 0} XP on return)`
              );
            } catch (err) {
              const msg = err?.response?.data?.detail || (language === 'ru' ? 'Не удалось назначить' : 'Assignment failed');
              toast.error(msg);
              throw err;
            }
          }}
        />

        {/* PR Manager card picker — sub-sheet stacked above */}
        <PRManagerCardPicker
          open={prPickerOpen}
          language={language}
          cards={(prState?.eligible_cards || []).filter(
            (c) => !prPickerExcludeRef.current.has(c.card_id)
          )}
          onPick={(c) => {
            const resolver = prPickerResolverRef.current;
            prPickerResolverRef.current = null;
            setPrPickerOpen(false);
            haptic && haptic('selection');
            if (resolver) resolver(c);
          }}
          onClose={() => {
            const resolver = prPickerResolverRef.current;
            prPickerResolverRef.current = null;
            setPrPickerOpen(false);
            if (resolver) resolver(null);
          }}
        />

        {/* PR Manager rename — reuses the rename sheet with subject='pr_manager' */}
        <RestorerRenameSheet
          open={prRenameOpen}
          language={language}
          subject="pr_manager"
          currentName={prManagerName}
          onClose={() => setPrRenameOpen(false)}
          onConfirm={async (newName) => {
            const { toast } = await import('sonner');
            try {
              const inv = await api.post('/team/pr-manager/rename/invoice', { name: newName });
              const link = inv.data?.invoice_link;
              if (!link) throw new Error('No invoice link');
              const tg = window.Telegram?.WebApp;
              const status = await new Promise((resolve) => {
                if (tg?.openInvoice) {
                  tg.openInvoice(link, (st) => resolve(st));
                } else {
                  window.open(link, '_blank');
                  setTimeout(() => resolve('paid'), 800);
                }
              });
              if (status !== 'paid') {
                if (status === 'cancelled') {
                  toast(language === 'ru' ? 'Оплата отменена' : 'Payment cancelled');
                } else if (status === 'failed') {
                  toast.error(language === 'ru' ? 'Оплата не прошла' : 'Payment failed');
                }
                throw new Error(`status=${status}`);
              }
              const res = await api.post('/team/pr-manager/rename/confirm', { name: newName });
              if (res.data?.user) setUser(res.data.user);
              await refreshPrState();
              haptic && haptic('success');
              toast.success(language === 'ru' ? `Имя изменено на ${newName}` : `Renamed to ${newName}`);
            } catch (err) {
              if (!String(err?.message || '').startsWith('status=')) {
                toast.error(language === 'ru' ? 'Не удалось сменить имя' : 'Rename failed');
              }
              throw err;
            }
          }}
        />
      </div>
    );
  }

  // Empty state - no cards
  return (
    <div className="pb-24 px-4">
      {/* Header */}
      <div className="pt-4 pb-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5 glass-card px-3 py-1.5 rounded-full">
              <Coins className="w-4 h-4 text-[#D4AF37]" />
              <span className="font-mono text-sm tabular-nums">{(user?.coins || 0).toLocaleString()}</span>
            </div>
          </div>
          <button
            data-testid="profile-btn-empty"
            onClick={() => { haptic(); navigate('/profile'); }}
            className="w-8 h-8 rounded-full glass-card border border-white/10 flex items-center justify-center hover:border-[#D4AF37]/50 transition-colors"
          >
            {user?.photo_url
              ? <img src={user.photo_url} alt="" className="w-8 h-8 rounded-full object-cover" />
              : <User className="w-4 h-4 text-[#A1A1AA]" />
            }
          </button>
        </div>
      </div>

      {/* Empty state */}
      <div className="text-center py-12">
        <div className="glass-card rounded-2xl p-8">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#D4AF37]/10 flex items-center justify-center">
            <Gift className="w-10 h-10 text-[#D4AF37]" />
          </div>
          <h3 className="font-heading text-xl text-[#F5F5F7] mb-2">{t('no_cards')}</h3>
          <p className="text-[#A1A1AA] text-sm mb-6">
            {language === 'ru' 
              ? 'Купите случайную картину или пригласите друзей, чтобы получить картины в свою коллекцию!'
              : language === 'zh'
              ? '购买随机画作或邀请朋友来获得您的第一幅画作！'
              : 'Buy a random painting or invite friends to get paintings in your collection!'}
          </p>
          {hasAvailableCards && (
            <button
              data-testid="buy-random-btn"
              onClick={handleBuyRandom}
              disabled={buying || showReel}
              className="bg-gradient-to-r from-[#D4AF37] to-[#F5D07A] text-[#0C0C14] px-6 py-3 rounded-xl font-bold flex items-center justify-center gap-2 mx-auto disabled:opacity-50 hover:shadow-[0_0_12px_rgba(212,175,55,0.5)] active:scale-95 transition-all"
            >
              <Gift className="w-5 h-5" />
              <span>{t('buy_random_card')}</span>
            </button>
          )}
          {!hasAvailableCards && (
            <p className="text-[#A1A1AA] text-sm">
              {language === 'ru' 
                ? 'Пока нет доступных картин. Ожидайте добавления!'
                : language === 'zh'
                ? '暂无可用画作。请等待添加！'
                : 'No paintings available yet. Stay tuned!'}
            </p>
          )}
        </div>
      </div>

      {/* New Card Reveal Animation */}
      {newCard && (
        <CardRevealModal
          card={newCard}
          onComplete={handleCardRevealComplete}
          language={language}
        />
      )}

      {/* Wheel-of-fortune reel (empty-state entry point) */}
      {showReel && (
        <RandomPaintingReel
          buyCost={buyCost}
          tutorial={reelTutorial}
          initialPool={reelPool}
          onPoolFetched={setReelPool}
          onPurchased={handleReelPurchased}
          onClose={handleReelClose}
        />
      )}
    </div>
  );
};

const RARITY_EXHIBIT_BONUS = {
  common: 0, uncommon: 0.05, rare: 0.10, legendary: 0.25, masterpiece: 1.00,
};

const ExhibitPickerModal = null;  // Deprecated — direct Exhibit/Return flow replaces it.

const CardDetailModal = ({ card, onClose, t, language, user, haptic, onUpgrade, onExhibitDone, tutorialStep, setTutorialStep, loadCards, refreshUser, initialXpPreview }) => {
  // Pull `consumePrefetch` straight from the hook so we can hydrate the
  // mint-price / ton-network straight from the GameContext boot cache
  // and skip the /ton/info round-trip that was making the "Make TON
  // Artwork" button pop in with a 200-500ms delay on first modal open.
  const { consumePrefetch } = useGame();
  const [currentCard, setCurrentCard] = useState(card);
  const [repairing, setRepairing] = useState(false);
  const [showRepairAnim, setShowRepairAnim] = useState(false);
  const [flipped, setFlipped] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [fadeIn, setFadeIn] = useState(false);
  const [closing, setClosing] = useState(false);
  const [burning, setBurning] = useState(false);
  const [showRarityTable, setShowRarityTable] = useState(false);
  const [sellTooltip, setSellTooltip] = useState(false);
  const [sellModalOpen, setSellModalOpen] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showCropEditor, setShowCropEditor] = useState(false);
  const [auctionHistory, setAuctionHistory] = useState(null);

  // Probe whether current user can edit crops (admin only)
  useEffect(() => {
    let cancelled = false;
    api.get('/admin/check')
      .then((res) => { if (!cancelled) setIsAdmin(!!res.data?.is_admin); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Lazy-load auction history for this template when Details panel opens
  useEffect(() => {
    if (!detailsOpen || !currentCard.template_id || auctionHistory) return;
    let cancelled = false;
    api.get(`/auction/template-history/${currentCard.template_id}`)
      .then((res) => { if (!cancelled) setAuctionHistory(res.data || null); })
      .catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailsOpen, currentCard.template_id]);
  // Use null until /ton/info responds so the price is not rendered as a
  // stale default (preview env returns 25, production 50; either way the
  // initial paint must not show an incorrect number that "self-corrects"
  // a moment later — players read that as the price changing on them).
  // The endpoint is now part of the GameContext boot-prefetch list, so
  // by the time this modal mounts the value is already in memory and
  // the "Make TON Artwork" button paints in the very first frame.
  // Background re-fetch below is kept as a fallback in case the boot
  // prefetch errored (e.g. cold-start during TON RPC hiccup).
  const tonInfoBoot = useMemo(
    () => (consumePrefetch ? consumePrefetch('/ton/info') : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  const [mintPrice, setMintPrice] = useState(
    typeof tonInfoBoot?.mint_price_stars === 'number' ? tonInfoBoot.mint_price_stars : null
  );
  const [tonNetwork, setTonNetwork] = useState(tonInfoBoot?.network || 'mainnet');

  useEffect(() => {
    // Skip the round-trip if the boot prefetch already populated state.
    if (mintPrice != null) return;
    api.get('/ton/info').then(r => {
      if (typeof r.data?.mint_price_stars === 'number') setMintPrice(r.data.mint_price_stars);
      if (r.data?.network) setTonNetwork(r.data.network);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    document.body.classList.add('modal-open');
    requestAnimationFrame(() => setFadeIn(true));
    return () => document.body.classList.remove('modal-open');
  }, []);

  const isTutorialLocked = tutorialStep === 'repair' || tutorialStep === 'exhibit';

  const handleClose = () => {
    if (isTutorialLocked) return;
    setClosing(true);
    setFadeIn(false);
    setTimeout(() => onClose(), 400);
  };

  const handleRepair = async () => {
    if (repairing) return;
    if (haptic) haptic();
    setRepairing(true);
    // OPTIMISTIC UPDATE: snap durability to 100 right now so the bar fills
    // the moment the player taps. The server call still validates and may
    // roll us back on failure. Was previously waiting on the network round
    // trip → tutorial felt "stuck for a second" before the bar moved.
    const prevCard = currentCard;
    const optimisticCard = {
      ...currentCard,
      durability: 100,
      durability_updated_at: new Date().toISOString(),
    };
    setCurrentCard(optimisticCard);
    setShowRepairAnim(true);
    setTimeout(() => setShowRepairAnim(false), 800);
    try {
      const res = await api.post(`/cards/${currentCard.id}/repair`);
      if (res.data.card) {
        // Reconcile with the authoritative server card (preserves the
        // optimistic 100% — server returns the same).
        setCurrentCard(res.data.card);
        if (onUpgrade) onUpgrade(res.data.card);
        if (tutorialStep === 'repair' && setTutorialStep) {
          setTimeout(() => setTutorialStep('exhibit'), 600);
        }
      }
    } catch (err) {
      // Roll back the optimistic update on failure
      setCurrentCard(prevCard);
      const msg = err.response?.data?.detail;
      if (msg === 'Not enough coins') {
        alert(t('not_enough_coins'));
      } else {
        alert(msg || t('error'));
      }
    } finally {
      setRepairing(false);
    }
  };

  const [showExhibitPicker, setShowExhibitPicker] = useState(false);
  const [modalXpPreview] = useState(() => initialXpPreview && Object.keys(initialXpPreview).length > 0 ? initialXpPreview : generateXpPreview());
  const [modalExhibiting, setModalExhibiting] = useState(false);

  const [modalCollecting, setModalCollecting] = useState(false);

  const handleModalExhibit = async () => {
    if (modalExhibiting) return;
    if (haptic) haptic();
    setModalExhibiting(true);
    const isTutorial = tutorialStep === 'exhibit';
    // Build an optimistic snapshot of the post-exhibit card and pass
    // it to the gallery so the strip shows the timer immediately.
    const minutes = isTutorial
      ? 0.75
      // Match backend `BASE_EXHIBIT_MINUTES + level × EXHIBIT_MINUTES_PER_LEVEL`.
      : 120 + ((user?.upgrades?.exhibit_duration || 0) * 10);
    const startMs = Date.now();
    const endMs = startMs + minutes * 60_000;
    const liveDur = currentCard.durability ?? 100;
    const optimisticCard = {
      ...currentCard,
      exhibit_start: new Date(startMs).toISOString(),
      exhibit_ends: new Date(endMs).toISOString(),
      exhibit_duration: minutes / 60,
      exhibit_duration_minutes: minutes,
      exhibit_start_durability: liveDur,
      // Mirror server `EXHIBIT_COIN_MULT_PER_MINUTE`.
      exhibit_expected_reward: Math.round((currentCard.rating || 1) * minutes * 0.06 * (liveDur / 100)),
      exhibit_expected_decay: 0,
      exhibit_xp_reward: Math.max(2, Math.round(minutes / 30 * 8)),
    };
    // Fire the network request FIRST and await the server's
    // authoritative card before closing the modal. Without the await
    // there was a race: the gallery's follow-up `loadCards` could
    // resolve before `/exhibit` had been processed and would patch
    // the card back to its idle state, wiping the optimistic update.
    // The network call is fast (~100-200ms) — well within the existing
    // 400ms close animation, so perceived latency is unchanged.
    let serverCard = null;
    try {
      const tutorialParam = isTutorial ? '?tutorial=true' : '';
      const res = await api.post(`/cards/${currentCard.id}/exhibit${tutorialParam}`);
      serverCard = res.data?.card || null;
    } catch (err) {
      console.warn('modal exhibit error', err?.response?.data?.detail);
      setModalExhibiting(false);
      alert(err.response?.data?.detail || 'Error');
      return;
    }
    setShowExhibitPicker(false);
    setClosing(true);
    setFadeIn(false);
    setTimeout(() => {
      onClose();
      if (onExhibitDone) onExhibitDone(serverCard || optimisticCard);
      if (isTutorial && setTutorialStep) {
        setTutorialStep('earn');
      }
    }, 400);
    setModalExhibiting(false);
  };

  const handleModalReturnFromExhibit = async () => {
    if (modalExhibiting) return;
    if (haptic) haptic();
    setModalExhibiting(true);
    try {
      const res = await api.post(`/cards/${currentCard.id}/return-from-exhibit`);
      if (res.data.card) {
        setCurrentCard(res.data.card);
        if (onUpgrade) onUpgrade(res.data.card);
      }
    } catch (err) {
      alert(err.response?.data?.detail || 'Error');
    } finally {
      setModalExhibiting(false);
    }
  };

  const handleModalCollect = async () => {
    if (modalCollecting) return;
    if (haptic) haptic();
    setModalCollecting(true);
    try {
      const res = await api.post(`/cards/${currentCard.id}/collect-exhibit`);
      if (res.data.card) {
        setCurrentCard(res.data.card);
        if (onUpgrade) onUpgrade(res.data.card);
        setShowExhibitPicker(false);
        if (res.data.rarity_upgraded) {
          const label = RARITY_LABELS[res.data.new_rarity]?.[language] || RARITY_LABELS[res.data.new_rarity]?.en || res.data.new_rarity;
          setTimeout(() => {
            alert(`${t('level_upgraded')}: ${label}!`);
          }, 600);
        }
      } else if (res.data.status === 'collected_and_destroyed') {
        alert(t('card_destroyed'));
        handleClose();
      }
    } catch (err) {
      alert(err.response?.data?.detail || 'Error');
    } finally {
      setModalCollecting(false);
    }
  };

  const handleBurn = async () => {
    if (burning || !currentCard?.id) return;
    if (haptic) haptic();
    setBurning(true);
    try {
      const res = await api.post(`/cards/${currentCard.id}/burn/invoice`);
      const invoiceLink = res.data?.invoice_link;
      if (!invoiceLink) throw new Error('No invoice');
      
      const tg = window.Telegram?.WebApp;
      if (tg?.openInvoice) {
        tg.openInvoice(invoiceLink, async (status) => {
          if (status === 'paid') {
            await api.post(`/cards/${currentCard.id}/burn/confirm`);
            await refreshUser();
            await loadCards();
            handleClose();
          }
          setBurning(false);
        });
      } else {
        window.open(invoiceLink, '_blank');
        setBurning(false);
      }
    } catch (err) {
      alert(err.response?.data?.detail || 'Error');
      setBurning(false);
    }
  };

  // ----- Withdraw from auction -----
  // Mirrors the flow in AuctionPage.handleWithdraw — kept inline here so
  // the player can also take a lot off-market straight from the card
  // preview (no need to navigate to the auction tab first).
  const handleWithdrawFromAuction = async () => {
    if (withdrawing) return;
    const lotId = currentCard.on_sale_lot_id;
    if (!lotId) {
      alert('Lot id missing — try reopening the painting.');
      return;
    }
    const isRu = language === 'ru';

    // Preview fee
    let preview;
    try {
      const { data } = await api.get(`/auction/sell/withdraw/preview/${lotId}`);
      preview = data;
    } catch (e) {
      alert(e.response?.data?.detail || (isRu ? 'Не удалось получить условия' : 'Could not preview'));
      return;
    }
    const feeText = preview.fee_stars > 0
      ? (isRu ? `Стоимость: ${preview.fee_stars} ⭐ (20% от текущей ставки)` : `Fee: ${preview.fee_stars} ⭐ (20% of current bid)`)
      : (isRu ? 'Бесплатно (ставок нет)' : 'Free (no bids yet)');
    const bidderText = preview.bidders_to_notify > 0
      ? (isRu ? `\n${preview.bidders_to_notify} участник(ов) получат уведомление.` : `\n${preview.bidders_to_notify} bidder(s) will be notified.`)
      : '';
    const confirmText = isRu
      ? `Снять лот с аукциона?\n\n${feeText}${bidderText}\n\nNFT останется у вас.`
      : `Withdraw this lot from auction?\n\n${feeText}${bidderText}\n\nNFT stays with you.`;
    if (!window.confirm(confirmText)) return;

    setWithdrawing(true);
    try {
      const { data } = await api.post('/auction/sell/withdraw', { lot_id: lotId });
      if (data.free) {
        try { haptic?.('success'); } catch (_) { /* noop */ }
        await loadCards?.();
        setWithdrawing(false);
        return;
      }
      const tg = window.Telegram?.WebApp;
      if (!tg?.openInvoice) {
        window.open(data.invoice_link, '_blank');
        setWithdrawing(false);
        return;
      }
      tg.openInvoice(data.invoice_link, async (status) => {
        if (status === 'paid') {
          try { haptic?.('success'); } catch (_) { /* noop */ }
          // give webhook time to process, then refresh
          await new Promise((r) => setTimeout(r, 2200));
          await loadCards?.();
        } else if (status === 'cancelled' || status === 'failed') {
          try {
            await api.post('/auction/sell/withdraw/cancel', { lot_id: lotId });
          } catch (_) { /* noop */ }
          await loadCards?.();
        }
        setWithdrawing(false);
      });
    } catch (err) {
      alert(err.response?.data?.detail || (isRu ? 'Снятие не удалось' : 'Withdraw failed'));
      setWithdrawing(false);
    }
  };

  // ----- Mint as NFT (TON) -----
  const [minting, setMinting] = useState(false);
  const [refreshingMeta, setRefreshingMeta] = useState(false);
  const handleRefreshNftMetadata = async () => {
    if (refreshingMeta || !currentCard?.id) return;
    if (haptic) haptic();
    setRefreshingMeta(true);
    const { toast } = await import('sonner');
    try {
      const res = await api.post('/users/me/refresh-nft-metadata', null, {
        params: { card_id: currentCard.id },
      });
      if (res.data?.pinged) {
        toast.success(language === 'ru'
          ? 'Запрос отправлен. Метадата обновится за 1–5 минут. В Tonkeeper потяните карточку NFT вниз.'
          : 'Refresh requested. Metadata updates in 1–5 min. In Tonkeeper pull the NFT card down.');
      } else {
        toast.error(language === 'ru'
          ? 'Индексатор не ответил, попробуйте позже.'
          : 'Indexer did not respond, try again later.');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Refresh failed');
    } finally {
      setRefreshingMeta(false);
    }
  };
  const handleMintNFT = async () => {
    if (minting || !currentCard?.id) return;
    if (haptic) haptic();
    if (!user?.ton_wallet) {
      alert(language === 'ru'
        ? 'Подключите TON-кошелёк в профиле, чтобы минтить NFT.'
        : 'Connect your TON wallet in Profile to mint NFTs.');
      return;
    }
    setMinting(true);
    try {
      const res = await api.post(`/cards/${currentCard.id}/mint/invoice`);
      const invoiceLink = res.data?.invoice_link;
      if (!invoiceLink) throw new Error('No invoice');
      const tg = window.Telegram?.WebApp;
      if (tg?.openInvoice) {
        tg.openInvoice(invoiceLink, async (status) => {
          if (status === 'paid') {
            try {
              await api.post(`/cards/${currentCard.id}/mint/confirm`);
              alert(language === 'ru'
                ? 'NFT минт запущен! Появится в кошельке через 1–2 минуты.'
                : 'NFT mint queued! It will appear in your wallet in 1–2 minutes.');
              for (let i = 0; i < 30; i++) {
                await new Promise(r => setTimeout(r, 4000));
                try {
                  const s = await api.get(`/cards/${currentCard.id}/mint/status`);
                  if (s.data?.status === 'minted') {
                    await loadCards();
                    setCurrentCard(prev => ({ ...prev, ...s.data, nft_address: s.data.nft_address }));
                    break;
                  }
                } catch (_) { /* noop */ }
              }
            } catch (e) {
              alert(e.response?.data?.detail || 'Mint confirmation failed');
            }
          }
          setMinting(false);
        });
      } else {
        window.open(invoiceLink, '_blank');
        setMinting(false);
      }
    } catch (err) {
      alert(err.response?.data?.detail || 'Error');
      setMinting(false);
    }
  };


  const durability = computeLiveDurability(currentCard, user?.upgrades?.durability_slow);
  const rating = currentCard.rating || 1;
  const repairCost = durability >= 99 ? 0 : Math.max(1, Math.round(((100 - durability) / 10) * rating * 4));
  const modalLvl = currentCard.upgrade_level || 0;

  // Presence-based (see the gallery tile comment): busy until the
  // server clears `exhibit_ends`, so modal actions can't hit
  // "Painting is being exhibited" on clock-skewed devices.
  const isExhibiting = !!currentCard.exhibit_ends;
  const exhibitDone = currentCard.exhibit_ends && new Date(currentCard.exhibit_ends) <= new Date();
  const modalTierColor = modalLvl >= 5 ? '#E5E4E2' : modalLvl >= 3 ? '#D4AF37' : modalLvl >= 1 ? '#CD7F32' : '#D4AF37';
  const modalTierName = modalLvl >= 5 
    ? (language === 'ru' ? 'Платина' : 'Platinum') 
    : modalLvl >= 3 
    ? (language === 'ru' ? 'Золото' : 'Gold') 
    : modalLvl >= 1 
    ? (language === 'ru' ? 'Бронза' : 'Bronze') 
    : '';

  return (
    <div 
      className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center px-10"
      style={{
        paddingBottom: '10px',
        transition: 'opacity 0.4s ease-in-out',
        opacity: fadeIn ? 1 : 0,
      }}
      onClick={handleClose}
    >
      {/* Sticky close button */}
      {!isTutorialLocked && (
        <button
          data-testid="close-modal-btn"
          onClick={handleClose}
          className="absolute z-[60] w-11 h-11 rounded-full glass flex items-center justify-center"
          style={{ top: 'calc(50% - 42vh)', right: '28px' }}
        >
          <span className="text-xl text-[#F5F5F7]">×</span>
        </button>
      )}

      <div 
        className="bg-[#0C0C14] w-full max-w-[280px] rounded-2xl max-h-[82vh] overflow-auto relative"
        style={{
          marginTop: '-5px',
          transition: 'transform 0.4s ease-in-out, opacity 0.4s ease-in-out',
          transform: fadeIn ? 'scale(1) translateY(0)' : 'scale(0.9) translateY(20px)',
          opacity: fadeIn ? 1 : 0,
          ...(modalLvl > 0 ? {
            border: `2px solid ${modalTierColor}66`,
            boxShadow: `0 0 ${10 + modalLvl * 3}px ${modalTierColor}33`
          } : {})
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Image with flip */}
        <div className="aspect-[4/5] relative" style={{ perspective: '800px' }}>
          <div
            className="w-full h-full transition-transform duration-500 cursor-pointer"
            style={{
              transformStyle: 'preserve-3d',
              transform: flipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
            }}
            onClick={() => { haptic && haptic(); setFlipped(!flipped); }}
          >
            {/* Front - Image */}
            <div className="absolute inset-0 w-full h-full rounded-t-2xl overflow-hidden" style={{ backfaceVisibility: 'hidden' }}>
              {modalLvl > 0 && (
                <>
                  <div 
                    className="absolute inset-0 rounded-t-2xl z-10 pointer-events-none"
                    style={{
                      boxShadow: `inset 0 0 ${8 + modalLvl * 3}px ${modalTierColor}55`,
                      borderBottom: 'none',
                      borderRadius: '0.875rem 0.875rem 0 0',
                    }}
                  />
                  <div 
                    className="absolute top-2 left-2 z-20 flex items-center gap-1 px-2 py-0.5 rounded-md"
                    style={{ 
                      background: `linear-gradient(135deg, ${modalTierColor}33, ${modalTierColor}55)`,
                      border: `1px solid ${modalTierColor}77`,
                      backdropFilter: 'blur(4px)'
                    }}
                  >
                    <ChevronRight className="w-3 h-3" style={{ transform: 'rotate(-90deg)', color: modalTierColor }} />
                    <span className="font-mono text-[10px] font-bold" style={{ color: modalTierColor }}>Lv.{modalLvl}</span>
                    <span className="text-[8px]" style={{ color: `${modalTierColor}AA` }}>{modalTierName}</span>
                  </div>
                </>
              )}
              {currentCard.animation_url ? (
                <video
                  src={currentCard.animation_url}
                  poster={currentCard.image_url}
                  autoPlay
                  loop
                  muted
                  playsInline
                  preload="metadata"
                  className="w-full h-full object-cover rounded-t-2xl transition-all duration-700"
                  style={{
                    objectPosition: `${currentCard.image_focus_x ?? 50}% ${currentCard.image_focus_y ?? 50}%`,
                  }}
                />
              ) : (
                <img
                  src={currentCard.image_url}
                  alt={currentCard.title}
                  className={`w-full h-full object-cover rounded-t-2xl transition-all duration-700`}
                  style={{
                    objectPosition: `${currentCard.image_focus_x ?? 50}% ${currentCard.image_focus_y ?? 50}%`,
                    filter: tutorialStep === 'repair'
                      ? 'sepia(0.04) contrast(0.975) brightness(0.97) saturate(0.95)'
                      : showRepairAnim ? 'brightness(1.25)' : 'none',
                  }}
                />
              )}
              {/* Rarity sparkle halo for TON-minted cards (detail modal) */}
              {currentCard.nft_address && !flipped && (
                <TonSparkles rarity={currentCard.rarity} sizeScale={1.2} />
              )}
            </div>

            {/* Back - collection set bonuses */}
            <div
              className="absolute inset-0 w-full h-full rounded-t-2xl overflow-hidden"
              style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}
            >
              <CardBuffPanel cardId={currentCard.id} active={flipped} />
            </div>
          </div>

          {/* UI overlays — placed OUTSIDE the flip container so they stay
              upright when the card flips (back side shows only artwork info). */}
          {currentCard.is_unique && !flipped && (
            <div
              data-testid="modal-unique-badge"
              title={language === 'ru' ? 'Единственный экземпляр' : 'One of a kind'}
              className="absolute top-3 left-3 z-[40] px-2 py-1 rounded-md text-[10px] font-bold tracking-[0.12em] pointer-events-none"
              style={{
                background: 'linear-gradient(135deg,#FACC15,#F59E0B)',
                color: '#0a0a12',
                boxShadow: '0 4px 12px rgba(250,204,21,0.4)',
              }}
            >
              UNIQUE
            </div>
          )}
          {currentCard.on_sale && !flipped && (
            <div
              data-testid="modal-on-sale-badge"
              title={language === 'ru' ? 'Выставлено на аукцион' : 'Listed on auction'}
              className={`absolute z-[40] px-2 py-1 rounded-md text-[10px] font-bold tracking-[0.12em] pointer-events-none animate-pulse ${currentCard.is_unique ? 'top-12 left-3' : 'top-3 left-3'}`}
              style={{
                background: 'linear-gradient(135deg,#A855F7,#7C3AED)',
                color: '#fff',
                boxShadow: '0 4px 12px rgba(168,85,247,0.5)',
              }}
            >
              ON SALE
            </div>
          )}
          {(isAdmin || currentCard.owner_id === user?.id) && currentCard.template_id && !flipped && (
            <button
              onClick={(e) => { e.stopPropagation(); setShowCropEditor(true); }}
              className="absolute top-3 right-10 z-[40] w-8 h-8 rounded-full bg-black/55 backdrop-blur-sm border border-white/20 flex items-center justify-center text-white hover:bg-black/75 transition"
              data-testid="card-edit-crop-btn"
              title="Edit crop"
            >
              <Crop className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {/* Details */}
        <div className="p-3.5">
          {/* Rarity bar - width reflects condition */}
          <div className="h-0.5 rounded-full mb-2.5 bg-[#1a1a2e]">
            <div
              className="h-full rounded-full transition-all duration-700"
              style={{
                width: `${durability}%`,
                backgroundColor: RARITY_COLORS[currentCard.rarity] || '#9CA3AF',
              }}
            />
          </div>
          
          {/* Title + Rating */}
          <div className="flex items-start justify-between mb-2">
            <div className="min-w-0 flex-1 mr-2">
              <h2 className="font-heading text-base font-medium text-[#F5F5F7] truncate">
                {currentCard.title} <span className="font-mono text-xs" style={{ color: RARITY_COLORS[currentCard.rarity] || '#9CA3AF' }}>#{currentCard.instance_number || ''}</span>
              </h2>
              <p className="text-xs text-[#A1A1AA] truncate">{currentCard.artist}</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <div className="flex items-center gap-0.5 px-2 py-1 rounded-full bg-[#D4AF37]/10">
                <ChessRook className="w-3 h-3 text-[#D4AF37]" />
                <span className="font-mono text-sm text-[#D4AF37]">{getInfluenceLevel(currentCard.influence_xp || 0)}</span>
              </div>
              <GameTooltip text={language === 'ru' ? 'Мин. ценность' : 'Min. value'} position="bottom" group="card-header">
                <div
                  className="flex items-center gap-0.5 px-2 py-1 rounded-full bg-[#FACC15]/10 cursor-pointer"
                  data-testid={currentCard.auction_won_amount > 0 ? "auction-price-badge" : undefined}
                >
                  <Star className="w-3 h-3 text-[#FACC15] fill-[#FACC15] animate-star-pulse" />
                  <span className="font-mono text-sm text-[#FACC15]">
                    {cardValue(currentCard)}
                  </span>
                </div>
              </GameTooltip>
            </div>
          </div>

          {/* Dropdown toggle */}
          <div className="relative">
            <button
            data-testid="details-toggle"
            onClick={() => setDetailsOpen(!detailsOpen)}
            className="w-full flex items-center justify-center gap-1 py-1.5 mb-2 rounded-lg glass-card border border-white/5 hover:border-white/10 transition-all"
          >
            <span className="text-[10px] text-[#A1A1AA]">
              {t('details')}
            </span>
            <ChevronDown
              className={`w-3.5 h-3.5 transition-transform duration-300 text-[#A1A1AA] ${detailsOpen ? 'rotate-180' : ''}`}
            />
          </button>
          </div>

          {/* Collapsible details */}
          <div
            className="overflow-hidden transition-all duration-300"
            style={{ maxHeight: detailsOpen ? '600px' : '0px', opacity: detailsOpen ? 1 : 0 }}
          >
            <div className="grid grid-cols-2 gap-1.5 mb-2">
              <div
                className="glass-card p-2 rounded-lg cursor-pointer"
                onClick={(e) => { e.stopPropagation(); setShowRarityTable(!showRarityTable); }}
                data-testid="rarity-cell"
              >
                <p className="text-[9px] text-[#A1A1AA] mb-0.5">{language === 'ru' ? 'Уровень' : 'Level'} <span className="text-[8px]">▼</span></p>
                <p className="text-[10px] font-medium" style={{ color: RARITY_COLORS[currentCard.rarity] || '#9CA3AF' }}>
                  {RARITY_LABELS[currentCard.rarity]?.[language] || RARITY_LABELS[currentCard.rarity]?.en || currentCard.rarity}
                </p>
              </div>
              <GameTooltip text={t('supply_tooltip')} position="top" group="card-details">
                <div className="glass-card p-2 rounded-lg cursor-pointer">
                  <p className="text-[9px] text-[#A1A1AA] mb-0.5">{language === 'ru' ? 'Тираж' : 'Supply'}</p>
                  <p className="font-mono text-[10px] text-[#F5F5F7]">{Math.max(1, currentCard.rarity_supply || 0).toLocaleString()}</p>
                </div>
              </GameTooltip>
              <div className="glass-card p-2 rounded-lg">
                <p className="text-[9px] text-[#A1A1AA] mb-0.5">{t('year')}</p>
                <p className="font-mono text-xs text-[#F5F5F7]">{currentCard.year || ''}</p>
              </div>
              <div className="glass-card p-2 rounded-lg">
                <p className="text-[9px] text-[#A1A1AA] mb-0.5">{t('owner')}</p>
                <p className="text-[10px] text-[#D4AF37] truncate">@{currentCard.owner_username || '—'}</p>
              </div>
            </div>

            {/* Rarity Table */}
            {showRarityTable && (
              <div className="mb-2 glass-card rounded-lg p-2.5 border border-white/5" data-testid="rarity-table">
                <p className="text-[9px] text-[#A1A1AA] mb-1.5">{t('rarity_thresholds')}</p>
                {[
                  { key: 'common', lvl: '0–4' },
                  { key: 'uncommon', lvl: '5–9' },
                  { key: 'rare', lvl: '10–24' },
                  { key: 'legendary', lvl: '25–49' },
                  { key: 'masterpiece', lvl: '50+' },
                ].map(({ key, lvl }) => (
                  <div key={key} className={`flex items-center justify-between py-0.5 ${currentCard.rarity === key ? 'opacity-100' : 'opacity-50'}`}>
                    <span className="text-[10px] font-medium" style={{ color: RARITY_COLORS[key] }}>
                      {RARITY_LABELS[key]?.[language] || RARITY_LABELS[key]?.en}
                    </span>
                    <span className="font-mono text-[9px] text-[#A1A1AA]">Lv. {lvl}</span>
                  </div>
                ))}
              </div>
            )}

            {/* View Post - inside details */}
            {currentCard.post_url && (
              <a
                href={currentCard.post_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="post-link-btn"
                className="flex items-center justify-center gap-1.5 py-1.5 mb-2 rounded-lg glass-card border border-[#D4AF37]/20 hover:border-[#D4AF37]/50 transition-colors"
              >
                <ExternalLink className="w-3 h-3 text-[#D4AF37]" />
                <span className="text-[#D4AF37] text-xs">
                  {language === 'ru' ? 'Открыть оригинал' : 'View Original'}
                </span>
              </a>
            )}

            {/* Influence bar */}
            {(() => {
              const xp = currentCard.influence_xp || 0;
              const level = getInfluenceLevel(xp);
              const levelBase = getInfluenceXpForLevel(level);
              const nextCost = getInfluenceNextCost(level);
              const progress = nextCost > 0 ? ((xp - levelBase) / nextCost) * 100 : 0;
              const rarityThresholds = [
                { lvl: 0, key: 'common' },
                { lvl: 5, key: 'uncommon' },
                { lvl: 10, key: 'rare' },
                { lvl: 25, key: 'legendary' },
                { lvl: 50, key: 'masterpiece' },
              ];
              const nextRarity = rarityThresholds.find(r => r.lvl > level);
              const nextRarityText = nextRarity
                ? (language === 'ru'
                    ? `До ${RARITY_LABELS[nextRarity.key]?.ru}: Lv.${nextRarity.lvl}`
                    : `Next: ${RARITY_LABELS[nextRarity.key]?.en} at Lv.${nextRarity.lvl}`)
                : (t('max_rarity'));
              return (
                <GameTooltip text={nextRarityText} position="top" group="card-details">
                  <div className="mb-2 cursor-pointer" data-testid="influence-bar">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[9px] text-[#A1A1AA]">
                      {language === 'ru' ? 'Влияние' : 'Influence'}
                    </span>
                    <span className="font-mono text-[10px] text-[#D4AF37] flex items-center gap-1">
                      <ChessRook className="w-2.5 h-2.5 text-[#D4AF37]" />
                      Lv.{level}
                      <span className="text-[#52525B] ml-0.5">{xp - levelBase}/{nextCost}</span>
                    </span>
                  </div>
                  <div className="h-1.5 rounded-full bg-[#1a1a2e] overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-500 bg-[#D4AF37]"
                      style={{ width: `${Math.min(progress, 100)}%` }}
                    />
                  </div>
                  </div>
                </GameTooltip>
              );
            })()}

            {/* Condition bar */}
            <div className="relative">
            <GameTooltip text={t('condition_tooltip')} position="top" group="card-details">
              <div className="mb-2 cursor-pointer">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[9px] text-[#A1A1AA]">
                  {language === 'ru' ? 'Состояние' : 'Condition'}
                </span>
                <span className="font-mono text-[10px]" style={{
                  color: durability > 50 ? '#10B981' : durability > 20 ? '#F59E0B' : '#EF4444'
                }}>
                  {durability.toFixed(1)}%
                  {durability <= 50 && <Wrench className="w-3 h-3 inline ml-1 text-[#EF4444]" />}
                </span>
              </div>
              <div className="h-1.5 rounded-full bg-[#1a1a2e] overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{
                    width: `${durability}%`,
                    backgroundColor: durability > 50 ? '#10B981' : durability > 20 ? '#F59E0B' : '#EF4444'
                  }}
                />
              </div>
              </div>
            </GameTooltip>
            </div>

            {/* Auction history (collapsible footer in Details) */}
            {auctionHistory && auctionHistory.count > 0 && (
              <div
                className="mt-2 mb-1 glass-card rounded-lg p-2.5 border border-[#A855F7]/20"
                data-testid="auction-history"
              >
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-[10px] font-medium text-[#A855F7]">
                    {language === 'ru' ? '🔨 История аукционов' : '🔨 Auction history'}
                  </span>
                  <span className="font-mono text-[9px] text-[#A1A1AA]">
                    {language === 'ru' ? 'Продаж' : 'Sales'}: <span className="text-[#F5F5F7]">{auctionHistory.count}</span>
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-1 mb-2">
                  <div className="text-center">
                    <p className="text-[8px] text-[#A1A1AA]">{language === 'ru' ? 'Средняя' : 'Avg'}</p>
                    <p className="font-mono text-[10px] text-[#A855F7] flex items-center justify-center gap-0.5">
                      <Star className="w-2 h-2 fill-[#A855F7]" />
                      {auctionHistory.avg_price ?? '—'}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-[8px] text-[#A1A1AA]">{language === 'ru' ? 'Мин' : 'Min'}</p>
                    <p className="font-mono text-[10px] text-[#10B981] flex items-center justify-center gap-0.5">
                      <Star className="w-2 h-2 fill-[#10B981]" />
                      {auctionHistory.min_price ?? '—'}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-[8px] text-[#A1A1AA]">{language === 'ru' ? 'Макс' : 'Max'}</p>
                    <p className="font-mono text-[10px] text-[#F59E0B] flex items-center justify-center gap-0.5">
                      <Star className="w-2 h-2 fill-[#F59E0B]" />
                      {auctionHistory.max_price ?? '—'}
                    </p>
                  </div>
                </div>
                {auctionHistory.sales?.slice(0, 5).map((s) => {
                  const dt = s.sold_at ? new Date(s.sold_at) : null;
                  const dateStr = dt
                    ? `${String(dt.getDate()).padStart(2, '0')}.${String(dt.getMonth() + 1).padStart(2, '0')}.${String(dt.getFullYear()).slice(-2)}`
                    : '—';
                  return (
                    <div
                      key={s.lot_id}
                      className="flex items-center justify-between py-0.5 border-t border-white/5 first:border-t-0"
                    >
                      <span className="text-[9px] text-[#A1A1AA] truncate max-w-[55%]">
                        @{s.winner_username || '—'}
                      </span>
                      <span className="flex items-center gap-1.5">
                        <span className="text-[9px] text-[#52525B] font-mono">{dateStr}</span>
                        <span className="font-mono text-[10px] text-[#A855F7] flex items-center gap-0.5">
                          <Star className="w-2 h-2 fill-[#A855F7]" />
                          {s.sold_amount}
                        </span>
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Repair Button */}
          <div className="relative">
            {tutorialStep === 'repair' && (
              <>
              <div className="absolute -top-8 left-1/2 -translate-x-1/2 pointer-events-none z-50">
                <TutorialArrow direction="down" />
              </div>
              {/* Anchored to the button, not to the condition bar: that
                  bar lives inside the collapsed Details accordion, so
                  its hidden rect pushed the hint to the bottom of the
                  screen. */}
              <TutorialDialog
                text={t('tut_repair')}
                position="above"
              />
              </>
            )}
            <button
            data-testid="repair-btn"
            onClick={handleRepair}
            disabled={repairing || durability >= 99 || (tutorialStep && tutorialStep !== 'repair') || (!tutorialStep && (user?.coins || 0) < repairCost)}
            className={`w-full py-2 rounded-lg text-xs font-medium flex items-center justify-center gap-1.5 disabled:opacity-30 disabled:cursor-not-allowed transition-colors mb-2 ${
              showRepairAnim ? 'brightness-125' : ''
            } ${tutorialStep === 'repair'
                ? 'border border-[#10B981]/60 text-[#10B981] bg-[#10B981]/[0.06] shadow-[0_0_0_1px_rgba(16,185,129,0.35),0_0_20px_rgba(16,185,129,0.28)]'
                : durability < 100
                  ? 'bg-transparent border border-[#10B981]/30 text-[#10B981]/80 hover:border-[#10B981]/60 hover:text-[#10B981]'
                  : 'bg-transparent border border-[#52525B]/30 text-[#52525B]'
            }`}
          >
            <Wrench className="w-3 h-3" />
            <span>{t('repair')}</span>
            {durability < 100 && (
              <span className="font-mono opacity-80 flex items-center gap-0.5">
                {tutorialStep === 'repair' ? (t('repair_free') || 'Free') : <><Coins className="w-3 h-3" />{repairCost}</>}
              </span>
            )}
          </button>
          </div>

          {/* Exhibit Button */}
          {isExhibiting ? (
            <div className="space-y-1.5">
              <div data-testid="modal-exhibit-progress" className="w-full py-2 rounded-lg bg-transparent border border-[#D4AF37]/30 flex items-center justify-center gap-1.5">
                <Clock className="w-3 h-3 text-[#D4AF37]" />
                <span className="text-xs text-[#D4AF37] font-mono">
                  <ExhibitTimer endsAt={currentCard.exhibit_ends} />
                </span>
                <span className="text-[10px] text-[#A1A1AA]">
                  {t('exhibited')}
                </span>
              </div>
              <button
                data-testid="modal-return-exhibit-btn"
                onClick={handleModalReturnFromExhibit}
                disabled={modalExhibiting || tutorialStep === 'repair'}
                className="w-full py-2 rounded-lg bg-transparent border border-[#A1A1AA]/30 text-[#A1A1AA] text-xs font-medium flex items-center justify-center gap-1.5 transition-all active:scale-95 hover:border-[#A1A1AA]/60 hover:text-[#F5F5F7] disabled:opacity-30"
              >
                <span>{modalExhibiting ? '...' : (language === 'ru' ? '⤺ Вернуть с выставки' : '⤺ Return from Exhibit')}</span>
              </button>
            </div>
          ) : (
            // `exhibitDone` branch is gone — auto-commit handles it.
            <div className="relative">
              {tutorialStep === 'exhibit' && (
                <>
                <div className="absolute -top-8 left-1/2 -translate-x-1/2 pointer-events-none z-50">
                  <TutorialArrow direction="down" />
                </div>
                <TutorialDialog
                  text={t('tut_exhibit')}
                  position="above"
                />
                </>
              )}
              <button
                data-testid="modal-exhibit-btn"
              onClick={() => {
                haptic && haptic();
                handleModalExhibit();
              }}
              disabled={modalExhibiting || tutorialStep === 'repair'}
              className={`w-full py-2 rounded-lg bg-transparent border border-[#D4AF37]/30 text-[#D4AF37]/80 text-xs font-medium flex items-center justify-center gap-1.5 transition-all active:scale-95 hover:border-[#D4AF37]/60 hover:text-[#D4AF37] disabled:opacity-30 disabled:cursor-not-allowed ${
                tutorialStep === 'exhibit' ? 'tut-halo border-[#F5D07A]/70 text-[#F5D07A] bg-[#F5D07A]/[0.06]' : ''
              }`}
            >
              <Coins className="w-3 h-3" />
              <span>{modalExhibiting ? '...' : t('exhibit')}</span>
            </button>
            </div>
          )}

          {/* Mint NFT Button — show if not yet minted. We hold the button
              back until `mintPrice` has loaded from /ton/info so the price
              never paints as a stale default that then flickers to the
              real value. */}
          {!tutorialStep && !currentCard.nft_address &&
            (currentCard.nft_minting_pending || currentCard.nft_mint_verification_pending) && (
            <div
              data-testid="mint-pending-status"
              className="w-full py-2 mt-2 rounded-lg border border-[#0098EA]/25 bg-[#0098EA]/5 text-[#0098EA] text-[11px] font-medium flex items-center justify-center gap-1.5 text-center"
            >
              <Sparkles className="w-3 h-3 animate-pulse" />
              <span>
                {language === 'ru'
                  ? 'Минт отправлен — ждём подтверждения сети'
                  : 'Mint sent — waiting for network confirmation'}
              </span>
            </div>
          )}

          {!tutorialStep && !isExhibiting && !currentCard.nft_address && mintPrice != null &&
            !currentCard.nft_minting_pending && !currentCard.nft_mint_verification_pending && (
            <button
              data-testid="mint-nft-btn"
              onClick={handleMintNFT}
              disabled={minting}
              className="w-full py-2 rounded-lg bg-transparent border border-[#0098EA]/30 text-[#0098EA] text-xs font-medium flex items-center justify-center gap-1.5 transition-all active:scale-95 hover:border-[#0098EA]/60 hover:bg-[#0098EA]/5 disabled:opacity-40 mt-2"
            >
              {minting ? '...' : (
                <>
                  <Sparkles className="w-3 h-3" />
                  <span>Make TON Artwork</span>
                  <span className="opacity-70 inline-flex items-center gap-0.5">
                    <Star className="w-2.5 h-2.5" fill="#FACC15" stroke="#FACC15" />
                    {mintPrice}
                  </span>
                </>
              )}
            </button>
          )}

          {/* Sell / Withdraw Button. When the painting is currently listed
              on the player auction, swap the Sell button for a Withdraw
              one so the player can take it off-market without leaving
              the gallery. Backend already handles the fee split (free
              if no bids, 20% Stars fee if there are bids — handled
              inside `handleWithdrawFromAuction`). */}
          {!tutorialStep && !isExhibiting && (
            <div className="relative mt-2">
              {currentCard.on_sale ? (
                <button
                  data-testid="withdraw-from-auction-btn"
                  onClick={() => {
                    haptic && haptic();
                    handleWithdrawFromAuction();
                  }}
                  disabled={withdrawing}
                  className="w-full py-2 rounded-lg bg-transparent border border-[#F59E0B]/35 text-[#F59E0B] text-xs font-medium flex items-center justify-center gap-1.5 transition-all active:scale-95 hover:border-[#F59E0B]/70 hover:text-[#F59E0B] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <X className="w-3 h-3" />
                  <span>
                    {withdrawing
                      ? (language === 'ru' ? 'Снимаем...' : 'Withdrawing...')
                      : (language === 'ru' ? 'Снять с аукциона' : 'Withdraw from auction')}
                  </span>
                </button>
              ) : (
                <button
                  data-testid="sell-btn"
                  onClick={() => {
                    haptic && haptic();
                    const rarity = (currentCard.rarity || '').toLowerCase();
                    const listable = rarity === 'rare' || rarity === 'legendary' || rarity === 'masterpiece';
                    if (!listable) {
                      setSellTooltip(true);
                      setTimeout(() => setSellTooltip(false), 2500);
                      return;
                    }
                    if (!currentCard.nft_address) {
                      setSellTooltip(true);
                      setTimeout(() => setSellTooltip(false), 2500);
                      return;
                    }
                    setSellModalOpen(true);
                  }}
                  className="w-full py-2 rounded-lg bg-transparent border border-[#A855F7]/25 text-[#A855F7]/70 text-xs font-medium flex items-center justify-center gap-1.5 transition-all active:scale-95 hover:border-[#A855F7]/55 hover:text-[#A855F7]"
                >
                  <Coins className="w-3 h-3" />
                  <span>{t('sell')}</span>
                </button>
              )}
              {sellTooltip && (
                <div className="absolute -top-10 left-1/2 bg-[#1a1a2e] border border-[#A855F7]/30 rounded-lg px-3 py-1.5 shadow-lg whitespace-nowrap z-50"
                  style={{
                    // Keep the X-centring inline so the keyframe (which
                    // only animates opacity, see sellTooltipFade in
                    // index.css) can't wipe it mid-fade.
                    transform: 'translateX(-50%)',
                    animation: 'sellTooltipFade 2.5s ease-out forwards',
                  }}>
                  <span className="text-xs text-[#F5F5F7]">
                    {(() => {
                      const rarity = (currentCard.rarity || '').toLowerCase();
                      const listable = rarity === 'rare' || rarity === 'legendary' || rarity === 'masterpiece';
                      if (!listable) {
                        return <>Need <span className="text-[#F59E0B] font-semibold">Rare</span> level or higher</>;
                      }
                      return <>Make <span className="text-[#0098EA] font-semibold">TON</span> first</>;
                    })()}
                  </span>
                </div>
              )}
              {sellModalOpen && (
                <SellModal
                  card={currentCard}
                  language={language}
                  onClose={() => setSellModalOpen(false)}
                  onListed={async () => {
                    // Refresh card list — the sold card is now in escrow
                    await loadCards?.();
                  }}
                />
              )}
            </div>
          )}

          {/* NFT Minted badge */}
          {currentCard.nft_address && (
            <a
              data-testid="nft-minted-badge"
              href={`https://${(currentCard.nft_address || '').toLowerCase().includes('test') ? 'testnet.' : ''}tonviewer.com/${currentCard.nft_address}`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full py-2 rounded-lg bg-[#0098EA]/10 border border-[#0098EA]/40 text-[#0098EA] text-xs font-medium flex items-center justify-center gap-1.5 transition-all hover:bg-[#0098EA]/20 mt-2"
            >
              <Sparkles className="w-3 h-3" />
              <span>{language === 'ru' ? 'NFT на TON' : 'Minted on TON'}</span>
              <LinkIcon className="w-3 h-3" />
            </a>
          )}

          {/* Force indexers (TonAPI -> Tonkeeper/Getgems) to refetch our live
              metadata; without it wallets keep the snapshot from mint time */}
          {currentCard.nft_address && (
            <button
              data-testid="nft-refresh-metadata-btn"
              onClick={handleRefreshNftMetadata}
              disabled={refreshingMeta}
              className="w-full py-2 rounded-lg bg-transparent border border-[#0098EA]/25 text-[#0098EA]/70 text-xs font-medium flex items-center justify-center gap-1.5 transition-all active:scale-95 hover:border-[#0098EA]/50 hover:text-[#0098EA] disabled:opacity-40 mt-2"
            >
              <RefreshCw className={`w-3 h-3 ${refreshingMeta ? 'animate-spin' : ''}`} />
              <span>
                {refreshingMeta
                  ? (language === 'ru' ? 'Обновление…' : 'Refreshing…')
                  : (language === 'ru' ? 'Обновить метадату NFT' : 'Refresh NFT metadata')}
              </span>
            </button>
          )}

          {/* Burn Button - only if not in tutorial and has more than 1 card */}
          {!tutorialStep && !isExhibiting && (user?.cards_count || 0) > 2 && (
            <button
              data-testid="burn-btn"
              onClick={handleBurn}
              disabled={burning}
              className="w-full py-2 rounded-lg bg-transparent border border-[#EF4444]/20 text-[#EF4444]/60 text-xs font-medium flex items-center justify-center gap-1.5 transition-all active:scale-95 hover:border-[#EF4444]/40 hover:text-[#EF4444]/80 disabled:opacity-40 mt-2"
            >
              {burning ? '...' : (
                <>
                  <Flame className="w-3 h-3" />
                  <span>{language === 'ru' ? 'Сжечь' : 'Burn'}</span>
                  <span className="opacity-60">⭐ 20</span>
                </>
              )}
            </button>
          )}
        </div>
        {showCropEditor && currentCard.template_id && (
          <CropEditor
            mode={isAdmin ? 'admin' : 'owner'}
            cardId={currentCard.id}
            templateId={currentCard.template_id}
            imageUrl={currentCard.image_url}
            initialX={currentCard.image_focus_x ?? 50}
            initialY={currentCard.image_focus_y ?? 50}
            onClose={() => setShowCropEditor(false)}
            onSaved={(x, y) => {
              setCurrentCard((prev) => ({ ...prev, image_focus_x: x, image_focus_y: y }));
              loadCards?.();
            }}
          />
        )}
      </div>
    </div>
  );
};

export default GalleryPage;

// Floating reward particles for exhibiting cards
const FloatingRewards = () => {
  const [particles, setParticles] = useState([]);
  const idRef = useRef(0);

  useEffect(() => {
    const types = ['coin', 'star'];
    const spawn = () => {
      const id = idRef.current++;
      const type = types[Math.floor(Math.random() * types.length)];
      const left = 15 + Math.random() * 70;
      setParticles(prev => [...prev.slice(-2), { id, type, left }]);
      setTimeout(() => {
        setParticles(prev => prev.filter(p => p.id !== id));
      }, 2200);
    };
    spawn();
    const interval = setInterval(spawn, 1200 + Math.random() * 1800);
    return () => clearInterval(interval);
  }, []);

  return particles.map(p => (
    <div
      key={p.id}
      className="absolute z-20 pointer-events-none animate-reward-float"
      style={{ left: `${p.left}%`, top: '25%' }}
    >
      <div className={`flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[8px] font-bold ${
        p.type === 'coin'
          ? 'bg-[#D4AF37]/30 text-[#D4AF37]'
          : 'bg-[#D4AF37]/20 text-[#D4AF37]'
      }`}>
        {p.type === 'coin' ? '+¢' : '+♟'}
      </div>
    </div>
  ));
};

// Timer component that shows remaining time
const ExhibitTimer = ({ endsAt }) => {
  // Self-ticking: re-renders only this tiny text node once per second,
  // instead of relying on a full-page 1Hz heartbeat.
  const [, force] = useState(0);
  const end = new Date(endsAt);
  const diff = Math.max(0, Math.floor((end - new Date()) / 1000));
  const done = diff <= 0;

  useEffect(() => {
    if (done) return;
    const id = setInterval(() => force(n => (n + 1) | 0), 1000);
    return () => clearInterval(id);
  }, [endsAt, done]);

  const h = Math.floor(diff / 3600);
  const m = Math.floor((diff % 3600) / 60);
  const s = diff % 60;

  if (done) return 'Done!';
  return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

// Progress bar for exhibit timer — uses start+end for accurate progress
const ExhibitProgressBar = ({ endsAt, duration, startAt }) => {
  const [progress, setProgress] = useState(0);
  
  useEffect(() => {
    const end = new Date(endsAt).getTime();
    let start;
    if (startAt) {
      start = new Date(startAt).getTime();
    } else {
      start = end - (duration || 3) * 3600 * 1000;
    }
    const totalMs = end - start;
    if (totalMs <= 0) { setProgress(1); return; }
    
    const tick = () => {
      const now = Date.now();
      const elapsed = now - start;
      setProgress(Math.min(1, Math.max(0, elapsed / totalMs)));
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [endsAt, duration, startAt]);
  
  return (
    <div className="h-1 bg-[#141420] rounded-full overflow-hidden">
      <div
        className="h-full bg-[#D4AF37] rounded-full"
        style={{ width: `${progress * 100}%`, transition: 'width 1s linear' }}
      />
    </div>
  );
};


const EXHIBIT_CITY_KEYS = [
  'exhibited_in_paris', 'exhibited_in_new_york', 'exhibited_in_london',
  'exhibited_in_rome', 'exhibited_in_berlin', 'exhibited_in_tokyo',
  'exhibited_in_madrid', 'exhibited_in_vienna', 'exhibited_in_amsterdam',
  'exhibited_in_budapest', 'exhibited_in_dubai', 'exhibited_in_florence',
  'exhibited_in_los_angeles', 'exhibited_in_prague', 'exhibited_in_milan',
  'exhibited_in_barcelona', 'exhibited_in_zurich', 'exhibited_in_seoul',
  'exhibited_in_istanbul', 'exhibited_in_copenhagen', 'exhibited_in_singapore',
];

const ExhibitCityLabel = ({ t }) => {
  const [idx, setIdx] = useState(() => Math.floor(Math.random() * EXHIBIT_CITY_KEYS.length));
  const [fading, setFading] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setFading(true);
      setTimeout(() => {
        setIdx(prev => {
          let next;
          do { next = Math.floor(Math.random() * EXHIBIT_CITY_KEYS.length); } while (next === prev);
          return next;
        });
        setFading(false);
      }, 400);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <span style={{ opacity: fading ? 0 : 1, transition: 'opacity 0.4s ease' }}>
      {t(EXHIBIT_CITY_KEYS[idx])}
    </span>
  );
};
